/**
 * API client — spec §D (endpoints) and §F1 (server state / query keys).
 *
 * Everything goes through the same origin: nginx (§B3) serves the SPA and proxies
 * `/api/` to Django, so the base URL is a path, not a host. In `vite dev` the same
 * path is proxied to the compose stack (see vite.config.ts).
 */
import { QueryClient, type QueryFunction } from '@tanstack/react-query';
import { currentLanguage, DEFAULT_LANGUAGE, type Language } from './i18n';
import {
  isOverlayLevel,
  type Country,
  type ReportingLevel,
  type Role,
  type SessionUser,
} from './state';

export const API_BASE = '/api/v1';

/**
 * `?lang=` for the three localised catalogue endpoints — empty for English.
 *
 * English is the server's default and its fallback, so the parameter would be a no-op: an
 * omitted `lang=en` keeps every English URL, cache entry and access-log line exactly as it
 * was before French existed.
 */
export const langParam = (lang: Language): string =>
  lang === DEFAULT_LANGUAGE ? '' : `?lang=${encodeURIComponent(lang)}`;

/**
 * The three localised query keys, named so the fetchers can *take the language off the key
 * they were invoked under* rather than re-derive it.
 *
 * That is the whole trick that keeps this change small and correct: the fetcher is a
 * `QueryFunction`, TanStack hands it the key it is fetching for, and the URL is built from
 * that key's language. The key and the request can therefore never disagree — there is no
 * second read of `currentLanguage()` to be a tick out of date — and no call site has to
 * thread a language through, so `queryFn: fetchIndicators` still reads exactly as it did.
 */
export type ConfigKey = readonly ['config', Language];
export type IndicatorsKey = readonly ['indicators', Language];
export type LayersKey = readonly ['layers', Role, Language];

/** §D error envelope: `{"error": {"code": ..., "message": ..., "details": ...}}`. */
export interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(status: number, code: string, message: string, details: unknown, requestId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Typed GET against `/api/v1`. Throws `ApiError` carrying the §D envelope's code and
 * the `X-Request-Id` header (§D/§J4) so failures are traceable back to the api log.
 */
export async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  const requestId = res.headers.get('X-Request-Id');
  if (!res.ok) {
    let body: ApiErrorBody = {};
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error page (e.g. nginx 502) — fall through to the status text */
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? `http_${res.status}`,
      body.error?.message ?? res.statusText ?? 'Request failed',
      body.error?.details ?? null,
      requestId,
    );
  }
  return (await res.json()) as T;
}

/* ------------------------------------------------------- §D1/§I4 session & CSRF */

/**
 * Django's CSRF cookie. Deliberately *not* HttpOnly (settings/base.py) — DRF's
 * `SessionAuthentication` enforces CSRF on every unsafe method of a live session, and
 * the SPA is a static bundle with no server-rendered template to seed the token from,
 * so it has to read the cookie and echo it in `X-CSRFToken`.
 */
export const CSRF_COOKIE = 'csrftoken';
export const CSRF_HEADER = 'X-CSRFToken';

export function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(prefix)) return decodeURIComponent(entry.slice(prefix.length));
  }
  return null;
}

/** In-flight `/auth/csrf` request, shared by concurrent callers (see `ensureCsrf`). */
let csrfRequest: Promise<string | null> | null = null;

/**
 * Make sure a CSRF token exists before the first non-GET of the session.
 * `GET /auth/csrf` is a 204 whose only job is `Set-Cookie: csrftoken=…`; once the
 * cookie is there this is free, so callers can invoke it unconditionally.
 *
 * Concurrent callers share one request: two mutations firing on the same tick would
 * otherwise each seed a token, and the second `Set-Cookie` would replace the secret the
 * first one had already read into its header — a spurious CSRF failure that only shows
 * up under load.
 */
export async function ensureCsrf(): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;
  if (!csrfRequest) {
    csrfRequest = fetch(`${API_BASE}/auth/csrf`, { credentials: 'same-origin' })
      .then(() => readCookie(CSRF_COOKIE))
      .finally(() => {
        csrfRequest = null;
      });
  }
  return csrfRequest;
}

/** Typed non-GET against `/api/v1`, carrying the §I4 CSRF token. `null` body → no body. */
export async function sendJson<T>(
  path: string,
  body: unknown,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
): Promise<T | null> {
  const token = await ensureCsrf();
  const res = await fetch(`${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const requestId = res.headers.get('X-Request-Id');
  if (!res.ok) {
    let envelope: ApiErrorBody = {};
    try {
      envelope = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error page — fall through to the status text */
    }
    throw new ApiError(
      res.status,
      envelope.error?.code ?? `http_${res.status}`,
      envelope.error?.message ?? res.statusText ?? 'Request failed',
      envelope.error?.details ?? null,
      requestId,
    );
  }
  if (res.status === 204) return null;
  return (await res.json()) as T;
}

/** §D1 `{id,name,role,org,country_scope}` — defined next to `Role`, in `lib/state.ts`. */
export type { SessionUser };

export interface Session {
  user: SessionUser;
}

/** §D1 `GET /auth/me` — the boot call that decides the SPA's §I2 gating. */
export const fetchMe = () => getJson<Session>('/auth/me', { credentials: 'same-origin' });

/** §D1 `POST /auth/login`. Throws `ApiError` (401 `unauthenticated`, 429 `rate_limited`). */
export const login = (username: string, password: string) =>
  sendJson<Session>('/auth/login', { username, password }).then((s) => s as Session);

/** §D1 `POST /auth/logout` — 204, and the server drops the session cookie. */
export const logout = () => sendJson<null>('/auth/logout', undefined).then(() => undefined);

/* ------------------------------------------------------------------ §D1 meta */

export interface Domain {
  id: string;
  name: string;
  short_name: string;
  color: string;
  icon: string;
}

export type AccessLevel = 'full' | 'limited' | 'none';

/**
 * One §F5.1 basemap, as `/meta/config.basemaps` serves it.
 *
 * `maxZoom` is camelCase where the rest of this payload is snake_case, deliberately: it is
 * a Leaflet `TileLayer` option name and is passed straight through to `L.tileLayer`, so
 * renaming it on the wire would only mean renaming it back on arrival.
 *
 * The `key` is the §F1 `basemap` code the permalink's `bm=` carries; `label` is prose and is
 * translated per language like the rest of the payload.
 */
export interface BasemapConfig {
  key: string;
  label: string;
  url: string;
  attribution: string;
  maxZoom: number;
}

/**
 * The §F5.1 catalogue and the cold-boot basemap that goes with it.
 *
 * `default` names one of `list`'s keys — the deployment's own starting basemap, `light`
 * where CARTO tiles are configured and `streets` where they are not. It is served *with* the
 * list rather than beside it because it is a property of the list, and the two must not be
 * able to disagree.
 */
export interface BasemapsConfig {
  default: string;
  list: BasemapConfig[];
}

/**
 * One theme's row of S3's default table (`apps/meta/themes.py`), as `/meta/config` serves it.
 *
 * The **client's own table**, resolved server-side against the catalogue: `layers` never
 * names a row `/layers` does not carry, and the six flags are the departures from "an
 * ordinary theme with one default dataset". The explorer branches on the flags rather than
 * on the theme key, so a client revision is a server change and not a rebuild of this
 * bundle.
 */
export interface ThemeDefault {
  theme: string;
  /** §D5 layer ids, catalogue order — the theme's default datasets. */
  layers: string[];
  /**
   * §C indicator ids the theme opens on. Only the MELS **baseline variables** appear here
   * (GESI's extension pair, SLM's twelve crops): S3 moves contractual and thematic
   * indicators behind the Indicators icon, and a baseline variable is a survey measurement
   * the themes present as a dataset. See `DatasetsPane`.
   */
  indicators: string[];
  /** `rasters.EPOCH_KINDS` family a time slider should step through, or null. */
  series: string | null;
  /**
   * §D5 ids this theme's dataset selects must **not** list — the 9 Sep 2026 one-epoch switch
   * (`apps/meta/themes.py::HIDDEN_LAYERS`). Empty for every theme but Land Use, whose six are
   * the other land-cover rows a reader could mistake for the 2024 one. Serving-level only:
   * the rows are still in `/layers` and still reachable by every other surface.
   */
  hidden_layers?: string[];
  /** cadence → layer id, for a theme published at more than one interval (rainfall). */
  cadences: Record<string, string>;
  provisional: boolean;
  in_preparation: boolean;
  requires_signin: boolean;
  display_only: boolean;
  excludes_indicators: boolean;
  contractual_only: boolean;
  /** True for a **2025 MELS baseline sheet** rather than a §C1 domain — see `BaselineTheme`. */
  baseline: boolean;
  /** The workbook sheet behind a baseline theme (`apps/indicators/groups.py`), else null. */
  group_id: string | null;
  /** The client's own sentence about this theme's state, translated. */
  note: string | null;
}

/**
 * One 2025 MELS baseline sheet, as its own rail entry — client directive, 8 Sep 2026.
 *
 * The same row is mirrored into `themes` under `key`, so a baseline chip is an ordinary
 * theme everywhere below the rail. What lives only here is the pair of names the rail needs
 * and no other theme has: `label` is the sheet's full name (the accessible name and the
 * tooltip) and `short` is the word the chip prints.
 */
export interface BaselineTheme {
  /** `bl_<group_id>` — the key in `order` and in `themes`. */
  key: string;
  group_id: string;
  label: string;
  short: string;
  /**
   * The two-letter mark the chip prints where a domain prints its icon. **Not translated** —
   * it stands in for an icon, and an icon does not change with the interface language. Null
   * for a sheet the server has no mark for, and then the SPA derives one (`monogramOf`).
   */
  monogram: string | null;
  /** The sheet's member ids, in the MEL team's own block order. */
  indicators: string[];
  /** The variable the chip opens on — the sheet's first member. */
  default: string;
}

/** S3's reference layers — the outlines every theme draws over. */
export interface ReferenceLayerConfig {
  key: string;
  unit_type: UnitType;
  admin_level: number | null;
  default_on: boolean;
  /** True for a row that appears **only** once a filter is applied (watersheds, partnerships). */
  filter_only: boolean;
}

/** `/meta/config.theme_defaults` — the rail's order, the table, and the reference layers. */
export interface ThemeDefaultsConfig {
  /**
   * Rail order: All, the eight domains, the 2025 baseline sheets, Indicators last.
   *
   * The client's S3 clause puts the Indicators icon "after Digital Agriculture"; the 8 Sep
   * directive puts the baseline band between the two, so the icon is still last and the
   * baseline group is what now follows Digital Agriculture.
   */
  order: string[];
  themes: Record<string, ThemeDefault>;
  /** The baseline sheets, in order — the rail's third kind of entry. */
  baseline_themes?: BaselineTheme[];
  /**
   * Series layer ids whose **time slider is off** (`apps/meta/themes.py::LOCKED_SERIES`).
   *
   * The 9 Sep 2026 directive: Land Use shows the 2024 land cover and nothing else. The lock
   * is on the series rather than on the theme because a slider is a property of the drawn
   * dataset — locking it on Land Use alone would leave the same eight years steppable from
   * All. The `epochs` payload is untouched, so the per-year rows stay out of the datasets
   * list exactly as before.
   */
  locked_series?: string[];
  reference_layers: ReferenceLayerConfig[];
}

/**
 * One §I1 role with the client's persona display name (`apps/accounts/personas.py`).
 *
 * `role` is the code every enforcement path speaks; `label` and `members` are copy. Two
 * roles deliberately share a `label` — `members` is what tells them apart in a list.
 */
export interface Persona {
  role: Role;
  label: string;
  members: string[];
}

/** A persona the client named that no §I1 role grants — display only. */
export interface ReservedPersona {
  key: string;
  label: string;
  members: string[];
  reserved_for: string;
}

export interface MetaConfig {
  domains: Domain[];
  years: number[];
  countries: Record<string, string>;
  ramps: Record<string, string[]>;
  biv: string[][];
  access_matrix: Record<string, Record<string, AccessLevel>>;
  features: Record<string, boolean>;
  /**
   * §F5.1, server-owned since the CARTO key round. **Optional on the type, always present
   * on a current server**: `/meta/config` is memoised for five minutes and cached in the
   * browser for the same, so a client can hold a payload from before the block existed —
   * and `basemapsFromConfig` falls back to the built-in list for exactly that window.
   */
  basemaps?: BasemapsConfig;
  /**
   * S3 phase A. Optional for the same reason `basemaps` is — a cached payload from before
   * the block existed — and every reader of it (`themes.ts`) treats absence as "no theme
   * table yet" rather than as an empty one.
   */
  theme_defaults?: ThemeDefaultsConfig;
  /** S3's persona display names. See `Persona`; the machinery is unchanged. */
  personas?: Persona[];
  reserved_personas?: ReservedPersona[];
  access_matrix_labels?: {
    roles: Record<string, string>;
    classifications: Record<string, string>;
    levels: Record<string, string>;
    limited_rules: Record<string, string>;
  };
}

/**
 * §D1 `GET /meta/config[?lang=fr]`.
 *
 * **A query parameter, not `Accept-Language`.** The server honours both, but a header makes
 * the same URL mean two things, which every shared cache in front of Django then has to be
 * taught about with a `Vary` — and nginx's §B3 `/api/` location is a plain `proxy_pass` with
 * no `proxy_cache`, so a mis-keyed entry would be a silent, invisible-in-development way of
 * serving French to an English reader. One URL per answer keeps that impossible.
 *
 * `lang=en` is **omitted**: English is the server's default, so the English URL stays
 * byte-for-byte what it has always been (the same rule `fetchValues` applies to `mode`), and
 * nothing about an English session changes because French exists.
 */
export const fetchConfig: QueryFunction<MetaConfig, ConfigKey> = ({ queryKey: [, lang] }) =>
  getJson<MetaConfig>(`/meta/config${langParam(lang)}`);

/**
 * §D1 `/stats/overview`. **The keys are §D1's; what they count is a MELS activity.**
 *
 * `sites_by_type` is the activity-status donut (`type` is `realized`/`in_progress`/
 * `upcoming`, drafts excluded) and `operation_points` is the number of *located* ones —
 * i.e. the number of activities the mini-map's markers add up to. The names did not change
 * with the meaning because §D1 fixes them and this type is the contract with the server;
 * see `apps/meta/views.py::stats_overview` for the whole reasoning.
 */
export interface StatsOverview {
  /** Activities with a resolved location — §F4's first stat tile. */
  operation_points: number;
  /** Distinct org units carrying ≥1 activity (mostly, but not only, communes). */
  communes_reached: number;
  /** B.2.1 producers reached, cumulative to the current year. */
  farmers_engaged: number;
  datasets: number;
  indicators: number;
  /** Activities by status — the §F4 donut and its legend. Server-translated. */
  sites_by_type: { type: string; label: string; color: string; count: number }[];
  sites_by_country: { country: string; count: number }[];
  /** B.2.1 cumulative, one entry per §F1 year; `null` where a year has no data. */
  headline_trend: { indicator: string; values: (number | null)[] };
}

export type OverviewKey = readonly ['overview', Country, Language];

/**
 * **Language-keyed**, unlike the geometry endpoints: `sites_by_type[].label` is prose the
 * server translates, so the English and the French answers are two different payloads.
 */
export const fetchOverview: QueryFunction<StatsOverview, OverviewKey> = ({
  queryKey: [, country, lang],
}) =>
  getJson<StatsOverview>(
    `/stats/overview${qs({
      country: country === 'ALL' ? null : country,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/** §D1 `/audit/ticker` — the §F4 pill's lines, already derived and translated server-side. */
export interface TickerLine {
  text: string;
}

export type TickerKey = readonly ['ticker', Language];

export const fetchTicker: QueryFunction<TickerLine[], TickerKey> = ({ queryKey: [, lang] }) =>
  getJson<TickerLine[]>(`/audit/ticker${langParam(lang)}`);

/**
 * `/activities/points` — one feature per org unit, not per activity (the server aggregates
 * and caps at 200; see `apps/indicators/views.py::activity_points`).
 */
export interface ActivityPointProperties {
  unit_id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  country: string | null;
  count: number;
  /**
   * Modal status of the unit's activities. `null` under `?by=watershed` for a watershed
   * with no activities at all — a choropleth needs a class for zero, so the server sends
   * the feature rather than omitting it.
   */
  dominant_status: string | null;
  by_status: Record<string, number>;
  /** `?by=watershed` only: MELS' own watershed id, the column the roll-up grouped on. */
  watershed_id?: number;
  [key: string]: unknown;
}

/**
 * The grouping `?by=` asks for.
 *
 * `unit` is the default and the historical payload: one feature per org unit the location
 * resolver landed an activity on, which for two thirds of the registry is a *commune*.
 * `watershed` is the R2 roll-up — MELS' own `watershed_id` on every row, commune-located
 * ones included — and is a different question, not a filter on the same answer (NOTES #480).
 */
export type ActivityGrouping = 'unit' | 'watershed';

export type ActivityPointsFC = FeatureCollection<
  { type: 'Point'; coordinates: [number, number] },
  ActivityPointProperties
> & {
  /** Echoed back by the server under `?by=`; absent on the default payload. */
  by?: ActivityGrouping;
  /**
   * `?by=watershed` only: non-draft activities that belong to **no** watershed — MELS'
   * "Tous les bassins versants" pseudo-ids and the handful carrying none. `sum(count) +
   * unassigned` is the scope's whole registry, so a choropleth that does not say this
   * number is quietly short by it.
   */
  unassigned?: number;
};

export const fetchActivityPoints = (country: Country = 'ALL', by: ActivityGrouping = 'unit') =>
  getJson<ActivityPointsFC>(
    `/activities/points${qs({
      country: country === 'ALL' ? null : country,
      // The default is omitted so the unscoped URL is byte-for-byte the one the server has
      // been caching for a week.
      by: by === 'unit' ? null : by,
    })}`,
  );

/* ------------------------------------- S2 Overview: activities by programme pathway */

/** One pathway in the dictionary the server sends with the counts — translated, coloured. */
export interface Pathway {
  id: string;
  label: string;
  color: string;
}

/** The three counts, plus the two denominators that make them honest. */
export interface PathwayBlock {
  counts: Record<string, number>;
  /** Non-draft activities in this row that MELS has never given a category. */
  uncategorised: number;
  categorised: number;
  total: number;
}

export interface PathwayWatershed extends PathwayBlock {
  unit_id: string;
  watershed_id: number | null;
  name: string;
  country: string | null;
}

/**
 * `/activities/by-pathway` — **per watershed**, deliberately.
 *
 * S2's refresh rule recalculates every panel number on every pan, filter and selection.
 * Asking the server for the current scope's three totals would be a request per pan; being
 * handed all eleven basins' three totals once (well under 2 kB) lets the client sum whatever
 * the extent or the selection contains, for free. So this payload is fetched by country and
 * language and by nothing else — the extent is not in the URL, because it is not a question
 * the server is asked.
 */
export interface PathwaysResponse {
  by: 'pathway';
  pathways: Pathway[];
  watersheds: PathwayWatershed[];
  /** Activities belonging to no programme watershed — reported, never folded into a basin. */
  unassigned: PathwayBlock;
  totals: PathwayBlock;
  /** The MELS category → pathway mapping, with live counts. Rendered by nothing; auditable. */
  categories: { id: number; name: string | null; pathway: string; count: number }[];
}

export type PathwaysKey = readonly ['pathways', Country, Language];

export const fetchPathways: QueryFunction<PathwaysResponse, PathwaysKey> = ({
  queryKey: [, country, lang],
}) =>
  getJson<PathwaysResponse>(
    `/activities/by-pathway${qs({
      country: country === 'ALL' ? null : country,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/* ------------------------------- Explorer "All": activities by unit and category */

/**
 * The four MELS **pathways** — the grouping every category belongs to, and the only thing
 * the explorer's hover card colours by.
 *
 * A closed union rather than a bare `string` because the tint is a lookup on it and a typo
 * would otherwise paint a category grey in silence. `uncategorised` is in the union because
 * the server's dictionary can carry it; the counts never do (uncategorised activities are
 * excluded server-side, which is the client's own instruction).
 */
export type ActivityPathway =
  | 'agroecology'
  | 'markets_finance'
  | 'enabling_environment'
  | 'uncategorised';

/**
 * Which hierarchy the counts are grouped by — one grouping per §F5.8 boundary row.
 *
 * Client change, 23 September 2026: the hover card is now on the boundaries themselves, and
 * a boundary the reader is pointing at can be a country, a region, a commune, a watershed or
 * a partnership. So the endpoint groups, and the row asks for its own grouping.
 */
export type ActivityGroup = 'country' | 'region' | 'commune' | 'watershed' | 'partnership';

/** The grouping the endpoint answers when nobody asks for one — and the one dropped from the URL. */
export const DEFAULT_ACTIVITY_GROUP: ActivityGroup = 'country';

/**
 * One MELS activity category.
 *
 * `label` is **already localised** by the server (`?lang=`) — nothing on this side
 * translates a category name, which is why the query key below carries the language.
 *
 * `n` is the one field that is not a property of the category but of the *scope*: it is
 * present on a unit's own list (where it is that unit's count, always > 0) and absent
 * from the payload's twelve-row dictionary, which is the catalogue rather than a count.
 */
export interface ActivityCategory {
  id: number;
  code: string;
  label: string;
  pathway: ActivityPathway;
  /** Unit rows only: activities in this category, in this scope. Never 0. */
  n?: number;
}

/** One org unit's counts: what it is, how many, and of what. */
export interface ActivityUnit {
  /** The hierarchy unit id — the same id `/hierarchy/units` puts on its features. */
  id: string;
  /** ISO code for a country row; the server sends the unit's own short code otherwise. */
  iso: string;
  name: string;
  /** `[lon, lat]` — GeoJSON order. `null` for a unit the server has no centroid for. */
  centroid: [number, number] | null;
  /** Categorised activities in scope. */
  total: number;
  /** `n > 0` only, **sorted by `n` descending** — the hover card renders them in this order. */
  categories: ActivityCategory[];
}

export interface ActivitiesByUnit {
  group: ActivityGroup;
  year: number;
  unit: string | null;
  country: string | null;
  /** All twelve categories, counts aside — the dictionary, for a legend that wants one. */
  categories: ActivityCategory[];
  /** Units with at least one categorised activity. A unit at 0 is omitted. */
  units: ActivityUnit[];
  /** `group=country` only: the old name for `units`, kept as an alias by the server. */
  countries?: ActivityUnit[];
}

/**
 * Grouping- and language-keyed (the category labels are translated server-side) and keyed by
 * all three explorer filters, because all three change the numbers: the country selector
 * narrows the set of units, a selected unit narrows to that unit's activities, and the year
 * is the year the counts are for.
 */
export type ActivitiesByUnitKey = readonly [
  'activitiesByUnit',
  ActivityGroup,
  Country,
  string | null,
  /** `null` while the reader has not chosen a year: the counts span every year. */
  number | null,
  Language,
];

/**
 * `GET /activities/by-country` — the explorer's **All** theme, one row per unit of `group`.
 *
 * A `QueryFunction` over its own key for the reason the three catalogue fetchers are: the
 * URL is built from the key TanStack is fetching for, so the cache entry and the request can
 * never disagree about the grouping, the language or the scope.
 *
 * Every parameter is dropped when it is not asked for (`qs` skips `null`), so the unscoped
 * English read of the default grouping is the bare path — the one a shared cache can hit.
 */
export const fetchActivitiesByUnit: QueryFunction<ActivitiesByUnit, ActivitiesByUnitKey> = ({
  queryKey: [, group, country, unit, year, lang],
}) =>
  getJson<ActivitiesByUnit>(
    `/activities/by-country${qs({
      group: group === DEFAULT_ACTIVITY_GROUP ? null : group,
      country: country === 'ALL' ? null : country,
      unit,
      year,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/* ---------------------------------------- S2 Overview: Progress Against Targets */

/** One year of IFDC's trajectory. `cumulative_target` is the only figure anything compares
 *  against; `annual_target` is the year's increment and travels because it is in the source. */
export interface TargetRow {
  year: number;
  scope_type: 'programme' | 'country';
  scope_id: string | null;
  /**
   * The year's increment. `null` on an actuals-as-target row and on every country row IFDC
   * allocated (6 Oct 2026: the 2033 figure only, no yearly split).
   */
  annual_target: number | null;
  cumulative_target: number;
  /** Unit of measure as IFDC delivered it — `Farmers`, `Hectares`. Not an org unit. */
  unit: string;
  /**
   * Where the commitment is written down. A target with no provenance is not loadable.
   * Free text for a stored row; `actuals`, `apportioned` or `allocated` for one the server
   * derived (see `TargetsResponse`).
   */
  source: string;
  /** `foundation` (2026–2028), `acceleration` (2029–2031), `consolidation` (2032–2033). */
  phase: string | null;
}

/**
 * `/indicators/{id}/targets` — IFDC's cumulative trajectory, delivered 2026-09-09.
 *
 * `pending` is a claim about the **whole delivery**, not about the selected year: D.2 and
 * E.1 have no commitment at all and the chart shows nothing for them, whereas a 2024
 * selection on an indicator that does have a trajectory is `pending: false` with
 * `target: null`. The two are different sentences and the chart draws them differently.
 *
 * `country_allocation_pending` is the other one that matters. Asking for a country IFDC has
 * not allocated gets the **programme** figure back, flagged, so a caller renders it as a
 * reference line and never as that country's own bar. Where IFDC has allocated (B.2.1 and
 * C.2.1, 6 Oct 2026) the answer is the country's own and `allocated` is true; see
 * `docs/client-specs/2026-10-06-country-targets.md`.
 */
export interface TargetsResponse {
  indicator: ValuesIndicator;
  /** The year asked for, before the 2033 cap. */
  requested_year: number;
  /** The trajectory year actually read — capped at 2033, `null` before 2026. */
  year: number | null;
  capped: boolean;
  scope: { type: 'programme' | 'country'; id: string | null };
  country_allocation_pending: boolean;
  /**
   * The selected year's row, or `null` when the trajectory does not reach that year.
   *
   * Before 2026 the trajectory has no row and, on the client's 2026-09-15 instruction, the
   * cumulative **actual** stands as the target (`actuals_as_target: true`, `source:
   * "actuals"`, `annual_target: null`) — at programme scope, for an indicator with real
   * measurements. The bar then shows 100 % by construction, and the chart labels it so.
   */
  target: TargetRow | null;
  /** True when `target` is the year's own cumulative actual standing in for a commitment. */
  actuals_as_target: boolean;
  /**
   * True when a country was asked for and `target` is the programme commitment apportioned
   * by that country's share of the cumulative actuals (`source: "apportioned"`) — served
   * until IFDC allocates, and still `country_allocation_pending`.
   */
  apportioned: boolean;
  /**
   * True when the country has IFDC's own 2033 allocation (B.2.1 and C.2.1, 6 Oct 2026):
   * 2033 is the allocation and 2026–2032 the programme year scaled by the country's 2033
   * share (`source: "allocated"`). `trajectory` and `eop` are then the country's series.
   */
  allocated: boolean;
  /** The 2033 row — the end-of-programme ambition. */
  eop: TargetRow | null;
  /** All eight years, so a caller can state the ambition without a second request. */
  trajectory: TargetRow[];
  pending: boolean;
}

export type TargetsKey = readonly ['targets', string, ReportingLevel, number, Country];

export const fetchTargets: QueryFunction<TargetsResponse, TargetsKey> = ({
  queryKey: [, indicatorId, , year, country],
}) =>
  getJson<TargetsResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/targets${qs({
      year,
      country: country === 'ALL' ? null : country,
    })}`,
  );

/* -------------------------------------------------------------- §D2 hierarchy */

export type UnitType = 'admin' | 'watershed' | 'partnership';

/**
 * `simplify` — §D2. `z7` is the default and the only one inside the §J1 2 MB budget at
 * every level. `full` is not offered: it serialises the raw IFDC polygons (14 MB for the
 * 1048 communes) and the API rejects it with a 400.
 */
export type Simplify = 'z5' | 'z7' | 'z10';

export interface UnitProperties {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
  area_km2: number | null;
  [key: string]: unknown;
}

export interface Feature<G = unknown, P = Record<string, unknown>> {
  type: 'Feature';
  id?: string | number;
  geometry: G | null;
  properties: P;
}

export interface FeatureCollection<G = unknown, P = Record<string, unknown>> {
  type: 'FeatureCollection';
  features: Feature<G, P>[];
}

export type UnitsFC = FeatureCollection<unknown, UnitProperties>;

/**
 * §D2 `GET /hierarchy/units`.
 *
 * `parent` is §F5.8's cascading filter: the id of the unit the reader has filtered to, whose
 * subtree — that unit **and** everything under it — is the whole answer. It composes with
 * `type`/`level` rather than replacing them, so `(admin, 2, parent=BF57)` is "the communes of
 * Djoro" and `(admin, 2, parent=BF5701)` is "that one commune". `null` is the whole
 * programme, and then the request URL is byte-for-byte what it was before the cascade.
 */
export const fetchUnits = (
  type: UnitType,
  level?: number | null,
  country: Country = 'ALL',
  simplify: Simplify = 'z7',
  parent: string | null = null,
) =>
  getJson<UnitsFC>(
    `/hierarchy/units${qs({
      type,
      level: level ?? null,
      country: country === 'ALL' ? null : country,
      simplify,
      parent,
    })}`,
  );

/**
 * §F5.8 `GET /hierarchy/options` — `{id, name}` rows for a `<select>`, no geometry.
 *
 * The cascade's four dropdowns would otherwise be filled from `/hierarchy/units`, which
 * answers the same question about Burkina Faso's communes in 141 kB of polygons instead of
 * 4.9 kB of names — four times over, as the reader walks the cascade.
 */
export interface UnitOption {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
}

/**
 * `operational` — client review R6: *"the cascade dropdowns list only OPERATIONAL units — a
 * unit (or a descendant) carrying ≥1 located MELS activity or ≥1 approved MELS-sourced
 * indicator value."*
 *
 * Default **on**, because the cascade is the only caller: offering 1 048 communes for a
 * programme that reports from 67 of them is a list a reader cannot use, and the 981 others
 * lead to an empty map. `/hierarchy/units`, `/tiles` and `/search` do not take the flag —
 * the map still draws every boundary, which is the difference between "we do not work here"
 * and "this place does not exist".
 *
 * The one thing it will never do is answer with nothing: where the shortlist would be empty —
 * a stack between `ingest_ifdc` and its first MELS sync — the server returns the unfiltered
 * list and sets `X-Operational-Fallback: 1` (`apps/hierarchy/views.py::options`). So the rows
 * are always usable, and a caller that wants to *say* which of the two it got has a header to
 * read rather than a count to guess from.
 */
export const fetchUnitOptions = (
  type: UnitType,
  level: number | null = null,
  country: Country = 'ALL',
  parent: string | null = null,
  operational = true,
) =>
  getJson<UnitOption[]>(
    `/hierarchy/options${qs({
      type,
      level,
      country: country === 'ALL' ? null : country,
      parent,
      operational: operational ? 1 : null,
    })}`,
  );

export interface SiteProperties {
  id: string;
  name: string;
  site_type: string;
  status: string;
  [key: string]: unknown;
}

export const fetchSites = (country: Country = 'ALL', siteType?: string | null) =>
  getJson<FeatureCollection<unknown, SiteProperties>>(
    `/hierarchy/sites${qs({ country: country === 'ALL' ? null : country, type: siteType ?? null })}`,
  );

/* ------------------------------------------------------------- §D3 indicators */

export type Tier = 'contractual' | 'outcome' | 'other';

/** §D3 `GET /indicators` row. `code` and `full_name` are null for non-MEL indicators. */
export interface Indicator {
  id: string;
  code: string | null;
  name: string;
  full_name: string | null;
  unit: string;
  domain: string;
  tier: Tier;
  /**
   * How §E4 values a parent from its children. `'none'` is the third and it is not a third
   * roll-up rule: it says the series **has no roll-up** — a survey estimate is served at the
   * geographies it was estimated at and nowhere else, so an empty level is by design rather
   * than by an unreported year. The two surfaces that read it are the legend's empty state
   * and the analytics panel's; everything else treats all three the same.
   */
  aggregation: 'sum' | 'mean' | 'none';
  is_cumulative: boolean;
  /** Decimal places for display (§G3 / `fmtNum`); 0 = a count. */
  fmt: number;
  source: string;
  cadence: string | null;
  /**
   * §M3 disaggregation helper (`b21_female`, …) — a real indicator the dashboard charts
   * read, kept out of the picker. The endpoint already filters these out unless
   * `?include_hidden=1`, so a row reaching the SPA is normally `false`.
   */
  hidden: boolean;
  /**
   * §M3.5 — the indicator has **native** watershed-stratum values (MELS reports it per
   * `watershed_id`), so the Operational-hierarchy watershed radio may be enabled for it.
   * Not "the engine could area-weight one": §E4 can do that for anything.
   */
  has_watershed_values: boolean;
  /**
   * §D3 — the reporting levels this indicator has **stored** values at, in §F5.8 row order:
   * `[0, 1, 2]` for a seeded series, `[0, 'watershed']` for a 2025 baseline survey estimate,
   * `[]` for one holding nothing.
   *
   * The map's level has derived purely from the §F5.8 cascade since client review R5 — no
   * filter means regions — and the baseline is `aggregation: 'none'`, so it has no region
   * rows and no roll-up that could make any: every one of the 58 series painted an empty map
   * on the view a reader opens on. `lib/state.effectiveLevels` turns this into the set the
   * level snaps to, and it deliberately does **not** read it as gospel for a `sum`/`mean`
   * series, whose stored levels understate what §E4 will answer for.
   */
  levels: ReportingLevel[];
  /**
   * Which workbook sheet an `mb_*` baseline sub-indicator came off (`agroforestry`,
   * `crop_yield`, …) and that sheet's name in the interface language — `null` for every
   * indicator that is not one of the 58.
   *
   * The 2025 baseline is a catalogue of *sub-indicators*: eight agroforestry practices,
   * eleven soil practices, twenty crops, two halves of one gendered extension question. As
   * 58 flat picker rows they were unfindable; as ten labelled clusters they are a list.
   * Derived server-side from the id prefix — there is no column.
   */
  group_id: string | null;
  group_label: string | null;
  /**
   * The distinct provenances actually behind this indicator's approved values, sorted —
   * `['demo']`, `['mels']`, `['demo','mels']`, or `[]` when it has no values at all.
   *
   * Not the same field as `source` above, which is the §C1 catalogue's *declaration* of
   * where the indicator is meant to come from. The two disagree for `b21`/`c21`, which
   * declare `mels` and hold four seeded years plus two real ones — and the §F5.6 legend
   * foot is written from **this** one, so it stops labelling real MELS reporting as
   * simulated (M3 review I4).
   */
  sources: IndicatorSource[];
  /**
   * Whether the picker may let a reader map this indicator. Derived server-side from
   * `sources`: false when the indicator holds demo seed and **only** demo seed.
   *
   * A *display* flag, not a data one — the row is still in this payload, `/values`,
   * `/series` and `/export` all still answer for it, and nothing was deleted. The picker
   * greys it (`aria-disabled`, no fill/overlay/bivariate) because 25 of the 27 catalogue
   * indicators currently paint a confident choropleth over simulated numbers, and a client
   * cannot be expected to keep a `Demo` chip in mind while reading a map.
   *
   * An indicator with no values at all (`sources: []`) is **active**: it is not demo data,
   * it is a real indicator MELS has not published yet, and the time bar already says so.
   */
  active: boolean;
  /**
   * §D3: the programme years this indicator actually has approved values for. Since the
   * demo purge `b21`/`c21` hold 2025–26 only, so the §F5.10 time bar greys the rest —
   * an empty map on 2021 reads as a broken portal rather than as an unpublished year.
   * `[]` for an indicator with no values at all.
   */
  years: number[];
  /**
   * §D3: the year a cold load should **open** this indicator on — the latest year holding
   * at least half the best-reported year's stored rows — or `null` when it has no values.
   *
   * `years` was not enough to choose one. B.2.1's 2026 is a real year with real data (three
   * rows) and its 2025 is eighty, so "the newest year with data" opened the portal on a map
   * with a single painted polygon, which reads as broken rather than as a reporting year
   * that has barely begun. Every year in `years` is still selectable; this only says where
   * the slider starts (`state.applyDefaultYear`).
   */
  default_year: number | null;
}

/** A `sources` token (`apps.indicators.provenance`). `derived` is a value this platform
 * computed from a named input (M4: `spi`, SPI-3 at end-September from the CHIRPS archive) —
 * not `manual`, because nobody reported it, and not `other`, which is the word the API
 * reserves for a provenance it cannot name. `other` is a recorded but unrecognised
 * `source_ref` prefix — deliberately not folded into `manual`. */
export type IndicatorSource = 'mels' | 'demo' | 'manual' | 'derived' | 'other';

/** §D3 `GET /indicators[?lang=fr]` — see `fetchConfig` on why the language is a parameter. */
export const fetchIndicators: QueryFunction<Indicator[], IndicatorsKey> = ({
  queryKey: [, lang],
}) => getJson<Indicator[]>(`/indicators${langParam(lang)}`);

/**
 * The levels a **manual** indicator submission may be reported at
 * (`apps.indicators.submissions.LEVELS`, verbatim).
 *
 * Wider than `ReportingLevel`: §D3's two overlay hierarchies and the unit-less programme
 * pseudo-level are all things a reporter can be asked to report *at*, and `/current`,
 * `/templates` and the submission's own checks all read this same vocabulary. Kept as a
 * string union rather than reusing the map's level type, because `'programme'` has no
 * polygons and must never reach `/hierarchy/units`.
 */
export type SubmissionLevel = '0' | '1' | '2' | 'watershed' | 'partnership' | 'programme';

/** One row of `/indicators/{id}/current` — a unit and the two numbers on file for it. */
export interface IndicatorCurrentUnit {
  /**
   * The org-unit id, or `''` at `programme` level — the server answers one unit with an
   * empty id there, because the programme total is not stored against a place.
   */
  id: string;
  name: string;
  country: string | null;
  /** The approved value for `period`, or `null` when nothing is on file. */
  value: number | null;
  /** The approved value for the period before it — what the grid shows read-only. */
  previous: number | null;
}

/** §D3 `GET /indicators/{id}/current` — what is on file, before anyone submits. */
export interface IndicatorCurrentResponse {
  indicator_id: string;
  period: number;
  level: SubmissionLevel;
  units: IndicatorCurrentUnit[];
}

export interface IndicatorCurrentParams {
  period: number;
  level: SubmissionLevel;
  /** `'ALL'` or an empty string means every country — the parameter is simply not sent. */
  country?: string | null;
}

/**
 * §D3 `GET /indicators/{id}/current?period=&level=&country=` — the values panel's prefill.
 *
 * Not a `QueryFunction`: the four things that pick an answer are a panel's own four
 * selects, and threading them through a query key context would say less than passing
 * them. `queryKeys.indicatorCurrent` carries the same four so the cache still keys on
 * exactly what was asked.
 */
export const fetchIndicatorCurrent = (
  indicatorId: string,
  { period, level, country }: IndicatorCurrentParams,
) =>
  getJson<IndicatorCurrentResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/current${qs({
      period,
      level,
      country: country && country !== 'ALL' ? country : null,
    })}`,
  );

/**
 * §D3 `GET /indicators/templates` as a **URL**, not a fetch.
 *
 * The blank CSV is offered as an ordinary link: the browser's own download is what a
 * reporter expects from "download the template", and fetching it into a blob would cost a
 * memory copy, an object URL and a filename this side would have to re-invent — the server
 * already names the file in its `Content-Disposition`.
 */
export const indicatorTemplateUrl = ({
  indicator,
  level,
  period,
  country,
}: {
  indicator: string;
  level: SubmissionLevel;
  period: number;
  country?: string | null;
}): string =>
  `${API_BASE}/indicators/templates${qs({
    indicator,
    level,
    period,
    country: country && country !== 'ALL' ? country : null,
  })}`;

/**
 * The `/hierarchy/units` request a reporting level implies (§M3.5).
 *
 * One helper so the choropleth, the overlays and the legend can never disagree about
 * *which polygons* the current level means — `'watershed'` is the overlay hierarchy
 * (`type=watershed`, no admin level), everything else is `type=admin` at that level.
 */
export function unitsQueryFor(level: ReportingLevel): { type: UnitType; level: number | null } {
  return isOverlayLevel(level)
    ? { type: level, level: null }
    : { type: 'admin', level };
}

/**
 * §D3 `?mode=` — how a year is read (see `AppState.cumulative`).
 *
 * `annual` is the stored series: the value *for* that year. `cumulative` is Σ over every
 * programme year up to and including it, which is the figure MELS publishes on its own
 * dashboard. The server computes the running total from the same annual rows the map
 * paints, so the two readings can never disagree.
 */
export type ValueMode = 'annual' | 'cumulative';

export const ANNUAL: ValueMode = 'annual';
export const CUMULATIVE: ValueMode = 'cumulative';

/**
 * The mode to actually request for one indicator — the store's flag, narrowed by the
 * catalogue.
 *
 * `cumulative` is only meaningful for an `is_cumulative` series: the running total of a
 * rainfall anomaly or a soil-carbon stock is not a number. The server ignores the flag for
 * those anyway (and says so in the payload's `mode`), but narrowing it here is what keeps
 * the *query key* — and therefore the cache entry and the request URL — identical to the
 * plain annual one, so switching the checkbox never refetches an indicator it cannot
 * affect. `meta` is `null`/`undefined` while `['indicators']` is in flight; the answer is
 * `annual` then, and the query re-keys itself once the catalogue lands.
 */
export function modeFor(
  cumulative: boolean,
  meta: Pick<Indicator, 'is_cumulative'> | null | undefined,
): ValueMode {
  return cumulative && meta?.is_cumulative ? CUMULATIVE : ANNUAL;
}

/** The trimmed indicator header §D3 echoes back with a values payload. */
export type ValuesIndicator = Pick<
  Indicator,
  'id' | 'code' | 'name' | 'unit' | 'tier' | 'aggregation' | 'is_cumulative' | 'fmt'
>;

/**
 * §D3 `GET /indicators/{id}/values`. Units with no data are **omitted** from `values`
 * (Task 4 report §1), so the choropleth has to treat "absent" as no-data, not as zero.
 * `breaks_hint` is always null — §G1–G3 classify in the browser.
 */
export interface ValuesResponse {
  indicator: ValuesIndicator;
  year: number;
  /**
   * The reading actually served. Echoed back because the server may narrow it: asking for
   * `cumulative` on a non-cumulative indicator answers `annual`, and a caller must be able
   * to tell what the numbers mean rather than assume it got what it asked for.
   */
  mode: ValueMode;
  /**
   * `0|1|2`, `null` for a single overlay unit, `'programme'` (§F5.9) or `'watershed'`
   * (§M3.5 — the whole overlay level at once). Echoed back so a held-over payload from a
   * different level can be detected and dropped.
   */
  level: number | 'programme' | 'watershed' | null;
  values: {
    unit_id: string;
    value: number;
    /**
     * Provenance of the **stored** row for this exact cell, or `null` when there is none
     * (see `derived`). M3 review I4.
     */
    source: IndicatorSource | null;
    /**
     * The sample the estimate was computed on, for a survey-estimate indicator
     * (`aggregation: 'none'` — the MELS baseline). `null` for every other kind of value and
     * for every derived one: a rolled-up number has no sample behind it.
     *
     * It is on the wire because the choropleth cannot show it: 33.8 % of 3,580 households
     * and 41 % of 18 land in classes that look equally confident.
     */
    sample_n: number | null;
    /**
     * True when §E4 *computed* this number — rolled up from child units, or area-weighted
     * from a watershed's overlapping admin cells — rather than reading a stored value.
     * M3 review I5: a reported figure and an estimate of it are otherwise identical on the
     * wire.
     */
    derived: boolean;
  }[];
  breaks_hint: number[] | null;
}

/**
 * §F5.9's pseudo-unit: one scope-wide number above admin level 0, served by §E4's
 * `programme_value` (Σ of the level-0 values for a count indicator, their area-weighted
 * mean for a rate). It is both the `?level=`/`?scope=` token and the `unit_id` the answer
 * carries back — the scope has no `org_units` row to name.
 */
export const PROGRAMME_UNIT = 'programme';

/**
 * `parent` is §F5.8's filter, the numbers half of `fetchUnits`'s: the same subtree, so the
 * polygons and the values are the same set of units by construction. The server **filters**
 * §E4's whole-scope answer rather than re-aggregating it, so a unit's number is identical
 * scoped and unscoped — what changes is how the §G1 breaks spread across the visible ones.
 */
export const fetchValues = (
  indicatorId: string,
  level: ReportingLevel,
  year: number,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
  parent: string | null = null,
) =>
  getJson<ValuesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/values${qs({
      level,
      year,
      country: country === 'ALL' ? null : country,
      // Omitted when annual — that is the server's default, and an unchanged URL keeps
      // every existing cache entry, permalink and log line exactly as it was.
      mode: mode === ANNUAL ? null : mode,
      parent,
    })}`,
  );

/**
 * §D3 `GET /indicators/{id}/series?unit_id=` — the six-year series behind §F5.7's spark.
 * Every programme year is present; a year with no data is `null`, and the chart draws the
 * gap rather than closing it (a straight line across a missing year is a fabricated trend).
 */
export interface SeriesResponse {
  unit: { id: string; name: string };
  /** The reading served — see `ValuesResponse.mode`. */
  mode: ValueMode;
  years: number[];
  values: (number | null)[];
}

/**
 * In `cumulative` the series is the running total: leading years with no data stay `null`,
 * and from the first value on every year carries the total to date (so a year that reported
 * nothing repeats the previous point rather than opening a gap — the total did not vanish).
 */
export const fetchSeries = (indicatorId: string, unitId: string, mode: ValueMode = ANNUAL) =>
  getJson<SeriesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/series${qs({
      unit_id: unitId,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §F5.9 `GET /indicators/{id}/values?level=programme[&country=]` — the dock popover's big
 * number, aggregated **server-side** so it can never disagree with the dashboard's KPI
 * tile: §E4 decides sum-vs-mean from `indicators.aggregation`, and re-deciding that in the
 * browser would be a second implementation of the programme's headline figures.
 *
 * `values` is `[]`, not `[{value: null}]`, when nothing in scope reported — the same
 * "absent ≠ zero" rule as every other §D3 values payload.
 */
export const fetchProgrammeValue = (
  indicatorId: string,
  year: number,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
) =>
  getJson<ValuesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/values${qs({
      level: PROGRAMME_UNIT,
      year,
      country: country === 'ALL' ? null : country,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §F5.9 `GET /indicators/{id}/series?scope=programme[&country=]` — the same number for
 * each of the six programme years, behind the popover's "Trend 2021–2026" spark. Year-
 * independent, so a time-bar tick re-reads it from cache (see `queryKeys.programmeSeries`)
 * and it is also where the ▲/▼ delta's `value(year − 1)` comes from (§E4).
 */
export const fetchProgrammeSeries = (
  indicatorId: string,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
) =>
  getJson<SeriesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/series${qs({
      scope: PROGRAMME_UNIT,
      country: country === 'ALL' ? null : country,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §D3 `GET /indicators/values/bulk?ids=…&level=&year=&country=` — up to 6 indicators in one
 * round trip, keyed indicator → unit. Backs §F5.7's "Other indicators" rows, which would
 * otherwise be six `/values` requests for six numbers.
 */
export interface BulkValuesResponse {
  year: number;
  level: number;
  indicators: Record<string, Record<string, number | null>>;
}

export const fetchBulk = (
  ids: string[],
  level: number,
  year: number,
  country: Country = 'ALL',
) =>
  getJson<BulkValuesResponse>(
    `/indicators/values/bulk${qs({
      ids: ids.join(','),
      level,
      year,
      country: country === 'ALL' ? null : country,
    })}`,
  );

/**
 * §D3 `GET /indicators/export` — a URL, not a fetch. The endpoint answers with a CSV
 * attachment, so the browser's own download path (`<a href download>`) is the right
 * client: no blob in memory, no XHR to keep alive, and the file lands with the server's
 * filename. `unit_id` exports the whole series for one unit (§F5.7's "Export CSV");
 * `level` + `year` exports the choropleth as a table.
 */
export const exportCsvUrl = (
  params: Record<string, string | number | null | undefined>,
): string => `${API_BASE}/indicators/export${qs(params)}`;

/* --------------------------------------------------------- §D2 unit detail */

/** §D2 `GET /hierarchy/units/{id}` — the unit, its parent chain and its extent. */
export interface UnitDetail {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
  pcode: string | null;
  area_km2: number | null;
  source: string | null;
  version: string | null;
  valid: boolean;
  centroid: [number, number] | null;
  /** Human level name — `Country | Region | Commune | Watershed | Partnership` (§F5.7). */
  level_label: string;
  country_name: string | null;
  /** Nearest first (§D2). */
  parents: { id: string; name: string; level: number | null }[];
  crosswalk: Record<string, string>;
  /** `[minx, miny, maxx, maxy]` of `geom_z7`; null for a unit with no geometry. */
  bbox: [number, number, number, number] | null;
  /**
   * NASA POWER climate normals for this unit — `docs/DATA_SOURCES.md` §4, wave 5.
   *
   * `null` when nothing covers the unit: POWER is harvested for admin-0 and admin-1 only,
   * an admin-2 unit inherits its region's row (`inherited: true`, `unit_name` naming which
   * region answered), and a watershed or partnership — whose `parent_id` chain never
   * reaches an admin unit — genuinely has none.
   */
  climate: UnitClimate | null;
}

/** §D2's `climate` block — see `apps/datasets/climate.py`. */
export interface UnitClimate {
  source: string;
  /** The unit the figures are *for*, which is not always the one that was asked for. */
  unit_id: string;
  unit_name: string;
  inherited: boolean;
  /** `[first, last]` year of the baseline — 1991–2020, the CHIRPS one. */
  period: [number, number];
  years: number;
  /** Mean 2 m air temperature, °C. */
  t2m_c: number | null;
  /** OLS slope of the annual means, °C per decade. */
  t2m_trend_c_decade: number | null;
  /** All-sky surface irradiance in POWER's own MJ/m²/day, and the same in kWh/m²/day. */
  solar_mj: number | null;
  solar_kwh: number | null;
  t2m_monthly: (number | null)[];
  solar_monthly: (number | null)[];
}

export const fetchUnitDetail = (id: string) =>
  getJson<UnitDetail>(`/hierarchy/units/${encodeURIComponent(id)}`);

export interface SearchHit {
  id: string;
  name: string;
  level_label: string;
  country_name: string;
}

/** `parent` keeps the §F5.10 box inside the §F5.8 filter — see `apps/hierarchy/views.py`. */
export const searchUnits = (q: string, country: Country = 'ALL', parent: string | null = null) =>
  getJson<SearchHit[]>(
    `/hierarchy/search${qs({ q, country: country === 'ALL' ? null : country, parent })}`,
  );

/* ------------------------------------------------ Task 6: `/intelligence/…` (design §1.4) */

/** `GET intelligence/usecases` — one row per registered use case, in registry order. */
export interface IntelUseCase {
  id: string;
  title: string;
  question: string;
  description: string;
  kind: 'management' | 'technical';
  order: number;
  enabled: boolean;
  methodology_key: string;
  last_run: IntelRunRef | null;
}

/** The `last_run` a use case row (or `{uc}/summary`/`{uc}/methodology`) carries. */
export interface IntelRunRef {
  id: string;
  period: string | null;
  status: 'queued' | 'running' | 'done' | 'error';
  finished_at: string | null;
  config_version: string;
}

export const fetchIntelUseCases = (lang: Language = currentLanguage()) =>
  getJson<IntelUseCase[]>(`/intelligence/usecases${langParam(lang)}`);

/** `GET {uc}/summary` — the headline block plus the staleness the §6.1 config note reads. */
export interface IntelSummary {
  use_case: string;
  period: string | null;
  computed_at: string | null;
  config_version: string | null;
  /**
   * How many inputs the run read. The rows themselves belong to `{uc}/methodology`, which
   * is where the pane prints them; the summary carries the count alone (backend, 2 Oct),
   * so nothing here reads a payload it never renders.
   */
  n_inputs: number;
  /** The use case's own headline shape — `by_category`, `main_crops`, `limitations`, … */
  summary: Record<string, unknown>;
  /** True once `{uc}/config` has been PATCHed past the run's own `config_version`. */
  stale: boolean;
}

export const fetchIntelSummary = (useCase: string, lang: Language = currentLanguage()) =>
  getJson<IntelSummary>(`/intelligence/${encodeURIComponent(useCase)}/summary${langParam(lang)}`);

/** `GET {uc}/units?level=&category=&country=` — the choropleth's rows. */
export interface IntelUnitRow {
  unit_id: string;
  name: string;
  category: string;
  score: number;
  /**
   * Scoring-engine use cases only (UC1): each dimension's own answer for this unit —
   * `true` flagged, `false` not flagged, `null` not assessed. The constraint chips draw
   * the map straight off this rather than fetching one detail per unit.
   */
  flags?: Record<string, boolean | null>;
  /** Why a `null` flag is null — `no_survey_value`, `no_pixels`, `no_normal`. */
  reasons?: Record<string, string>;
  /**
   * UC2 launch (backend task-1 report §4.2): the three extras that let every UC2 chip be
   * painted from this one request — the relative cropland change 2017→2024 in percent, the
   * rainfall tendency (`wetter`/`drier`/`stable`) and the four practice-group adoption
   * percentages. Each one optional: a `uc1`/`uc3` row carries none of them, and a row from
   * the pre-launch `uc2` run carries none either (the pane then paints "not assessed").
   */
  cropland_delta_pct?: number | null;
  rain_tendency?: string | null;
  adoption?: Record<string, number | null>;
  /** Only on a not-assessed row: `no_pixels` (a watershed) or `level` (a country). */
  reason?: string | null;
  /**
   * UC1 chart round (2 Oct 2026, design §1.2): each constraint's own measured number for
   * this unit, so the Overview's per-unit bars read off the same one request the
   * choropleth does. On a watershed row `value` is the metric itself with its `unit` and
   * the `reference` the rule compared it against; on a country row it is the share of that
   * country's watersheds flagged, in percent, with no reference. Absent on a payload from
   * before the round — the chart then says it has nothing to draw rather than throwing.
   */
  metrics?: Record<string, IntelUnitMetric>;
  /**
   * UC2 chart round (design §2.2): the two per-unit numbers the units chart needs beyond
   * the launch three — the rainfall change over the period in percent and the vegetation
   * trend per year — and the land-cover change-class shares, each as a percentage of the
   * unit's area. Country rows carry `share_positive_pct` instead: the share of that
   * country's watersheds with positive change. All optional, for the same reason.
   */
  rain_change_pct?: number | null;
  ndvi_trend?: number | null;
  lc_change?: IntelLcChange;
  share_positive_pct?: number | null;
  /**
   * Scaling round (3 Oct 2026): the three numbers the "What the change is made of" block
   * draws, with the engine's own verdict on each of the scored ones. On a watershed row
   * they are that watershed's; on a country row they are the mean of its watersheds', and
   * the block's own `share_*_ok_pct` say how many of them passed each check.
   *
   * Optional like everything else here: a run from before the round carries none, and the
   * block then prints its pending note rather than drawing a chart of nothing.
   */
  change_drivers?: IntelUc2ChangeDrivers;
  /**
   * The country a watershed row belongs to, when the run serves it. The country-level
   * chips derive their shares from the watershed rows, and this is how a watershed is
   * attributed to a country; without it the pane falls back to the id convention
   * (`BFWS01` sits under `BF`).
   */
  country?: string | null;
}

/** One `metrics[dim]` entry of a `uc1` units row (2 Oct chart round). */
export interface IntelUnitMetric {
  value: number | null;
  unit?: string | null;
  /**
   * What the rule compared the value against: a single boundary for a tercile or a
   * `gte`/`lte` rule, the `[lo, hi]` pair for an `outside` rule (soil pH), and `null` on a
   * country roll-up row, which is a share and has nothing to be read against.
   */
  reference?: number | number[] | null;
  flagged?: boolean | null;
  /**
   * Which metric the value came from, for a dimension whose rule is `any` of several
   * (soil: organic carbon / pH / nutrient-holding capacity; rainfall: the 2025 total or
   * the anomaly). Absent where the dimension has one metric, and absent from a run that
   * does not serve it — the chart then names the unit alone.
   */
  metric?: string | null;
}

/** `lc_change` — the four change classes' shares of a unit's area, in percent. */
export interface IntelLcChange {
  gain?: number | null;
  loss?: number | null;
  veg_gain?: number | null;
  veg_loss?: number | null;
}

/** One `{uc}/units/{id}` `similar[]` row — UC2's precomputed top-3 (backend §3.3). */
export interface IntelSimilarRow {
  unit_id: string;
  name: string;
  distance: number;
  /** `1 − d / d_max`, 0–1, programme-wide so the bar means the same on every strip. */
  similarity: number;
  adoption: number | null;
  /** `reference − candidate` adoption points for the selected group; `null` if either side has none. */
  gap: number | null;
  learning_opportunity: boolean;
}

/** One year of a UC2 detail series — `pct` for cropland/vegetation, `mm` for rainfall. */
export interface IntelSeriesPoint {
  year: number;
  pct?: number | null;
  mm?: number | null;
}

/**
 * UC2 tab-analytics round (design `2026-10-02-uc2-tabs-analytics-design.md` §Summary
 * additions): the four blocks the engine precomputes onto `uc2/summary` so the Trends,
 * Practices and Similar tabs say something without a click and without a request of their
 * own. Every field is optional at every depth — the live run is the previous one until the
 * recompute lands, and a missing block makes a tab print its "nothing here yet" note
 * rather than throw.
 *
 * A **scope** is either `'programme'` or an admin0 code (`BF`, `ML`, `NE`, `NG`): the
 * watershed level reads the programme, the country level reads the country.
 */
export interface IntelUc2ClassSeries {
  cropland?: IntelSeriesPoint[];
  veg?: IntelSeriesPoint[];
  rain?: IntelSeriesPoint[];
}

/** One scope of `by_class_series`: the three series per class, and the two class sizes. */
export interface IntelUc2ClassSeriesScope {
  positive?: IntelUc2ClassSeries | null;
  no_pattern?: IntelUc2ClassSeries | null;
  all?: IntelUc2ClassSeries | null;
  n?: { positive?: number | null; no_pattern?: number | null } | null;
}

/** One scope of `adoption_by_class`: each class's adoption per group, and their difference. */
export interface IntelUc2AdoptionScope {
  positive?: Record<string, number | null> | null;
  no_pattern?: Record<string, number | null> | null;
  /** `positive − no_pattern`, in points. */
  diff?: Record<string, number | null> | null;
}

/** One side of a `contrast_pairs` entry. */
export interface IntelUc2ContrastSide {
  unit_id: string;
  name: string;
  category: string;
}

/**
 * One `contrast_pairs` entry: two alike watersheds of **different** classes, with the
 * adoption gap and the learning flag per practice group. `countries` is the codes the pair
 * touches, which is what the country level filters the list on.
 */
export interface IntelUc2ContrastPair {
  a: IntelUc2ContrastSide;
  b: IntelUc2ContrastSide;
  similarity: number;
  gaps?: Record<string, number | null>;
  learning?: Record<string, boolean>;
  countries?: string[];
}

/** One `greening_by_unit` row — the vegetation trend per year, with the unit's class. */
export interface IntelUc2GreeningUnit {
  unit_id: string;
  name: string;
  category: string;
  ndvi_trend: number | null;
}

/**
 * `change_drivers` — the two land readings the class is decided from, plus the rainfall
 * change that is only ever context. Each `_ok` is the engine's own verdict on its reading
 * (`true` passed, `false` did not, `null` nothing to judge), never re-derived here: the
 * band is the run's, and a bar coloured from a frontend threshold would disagree with the
 * map the moment a curator moves the config.
 */
export interface IntelUc2ChangeDrivers {
  /** Farmland change over the period, in percent of the first year. */
  cropland_change_pct?: number | null;
  /** Vegetated-cover change over the same period, the same way round. */
  veg_change_pct?: number | null;
  /** Rainfall change over the period, context only. */
  rain_change_pct?: number | null;
  cropland_ok?: boolean | null;
  veg_ok?: boolean | null;
  /** The satellite greening check, once the archive gate has passed; absent until then. */
  greening_ok?: boolean | null;
  /**
   * **Country rows only**: the share of that country's watersheds whose farmland and
   * cover checks passed, in percent. They live here rather than on the row because they
   * are the country's answer to the very checks this block is about, and a reader of the
   * row should never have to join two places to colour one bar. A mean alone cannot say
   * how many places sit behind it, which is what these two add.
   */
  share_cropland_ok_pct?: number | null;
  share_veg_ok_pct?: number | null;
  /** The same share for the greening check: a country carries no `greening_ok` of its own. */
  share_greening_ok_pct?: number | null;
}

/** Which watershed a scaling candidate would learn from, and how alike the two are (0-1). */
export interface IntelUc2ScalingLearnFrom {
  unit_id: string;
  name: string;
  similarity: number;
}

/**
 * One row of `scaling_candidates[scope]` (scaling round, 3 Oct 2026): a place where a
 * watershed with positive change sits close by and reports more of some practice group.
 * `score` is the engine's own 0-1 roll-up of the three readings and `verdict` its word for
 * it; the pane ranks and badges on those rather than scoring anything itself.
 */
export interface IntelUc2ScalingRow {
  unit_id: string;
  name: string;
  country?: string | null;
  learn_from?: IntelUc2ScalingLearnFrom | null;
  /** Points behind the learn-from watershed, per practice group. */
  practice_gap?: Record<string, number | null>;
  /** The group with the widest gap, and that gap in points. */
  best_group?: string | null;
  best_gap?: number | null;
  /** Whether the two watersheds' rainfall is close enough to read the gap against. */
  rain_comparable?: boolean | null;
  score?: number | null;
  verdict?: string | null;
}

/** The four blocks together, as they sit on `uc2/summary`'s own `summary` object. */
export interface IntelUc2SummaryBlocks {
  by_class_series?: Record<string, IntelUc2ClassSeriesScope>;
  adoption_by_class?: Record<string, IntelUc2AdoptionScope>;
  contrast_pairs?: IntelUc2ContrastPair[];
  greening_by_unit?: IntelUc2GreeningUnit[];
  /**
   * Scaling round (3 Oct 2026): the ranked candidates per scope, keyed exactly as
   * `by_class_series` is (`'programme'`, then one entry per admin0 code), so the Similar
   * tab reads the country it is on without a request of its own.
   */
  scaling_candidates?: Record<string, IntelUc2ScalingRow[]>;
}

export const fetchIntelUnits = (
  useCase: string,
  level: string,
  category: string | null = null,
  country: Country | null = null,
) =>
  getJson<IntelUnitRow[]>(
    `/intelligence/${encodeURIComponent(useCase)}/units${qs({
      level,
      category,
      country: country && country !== 'ALL' ? country : null,
    })}`,
  );

/** One `drivers[]` entry of `{uc}/units/{id}` — design §1.1's explanation payload. */
export interface IntelDriver {
  dimension: string;
  /** `null` is "not assessed" — every input of this dimension was missing for this unit. */
  flagged: boolean | null;
  value: number | string | null;
  /**
   * What the value was compared against: the tercile boundary for a tercile rule, the
   * threshold for `gte`/`lte`, the `[low, high]` pair for an `outside` rule.
   */
  reference: number | string | number[] | null;
  unit: string | null;
  source: string;
  period: string | number | null;
  note: string;
  /** Only when `flagged` is `null`: `no_survey_value` / `no_pixels` / `no_normal`. */
  reason?: string | null;
  /** How `reference` was arrived at — the scorecard's wording turns on this. */
  reference_kind?:
    | 'tercile_country'
    | 'tercile_programme_fallback'
    | 'tercile_programme'
    | 'absolute'
    | null;
  /** `"<metric_id>:<op>"` of the sub-rule that fired; `null` when nothing fired. */
  rule_hit?: string | null;
  /** Metrics shown beside the row and never scored (productivity's GAEZ yield gap). */
  evidence?: IntelDriverEvidence[];
  /**
   * Only on a `tercile_programme_fallback` row: how many units of this unit's own country
   * had a value for the metric — the group that was too small for a country-specific
   * third. Absent on every other row.
   */
  n_group?: number | null;
}

/** One `drivers[].evidence[]` entry — a measured number the rule deliberately ignores. */
export interface IntelDriverEvidence {
  metric_id: string;
  value: number | string | null;
  unit: string | null;
  period: string | number | null;
  note: string | null;
}

/** One `underlying[]` entry — a driver's source, resolved to something the pane can toggle. */
export interface IntelUnderlying {
  kind: 'layer' | 'indicator';
  id: string;
  dataset: string | null;
  title: string;
  url: string;
}

/** `GET {uc}/units/{unit_id}` — the Selected Area block's whole answer, one row. */
export interface IntelUnitDetail {
  unit: { id: string; name: string; level: string; country: string | null };
  category: string;
  category_label: string;
  score: number;
  drivers: IntelDriver[];
  values: Record<string, unknown>;
  /** The enclosing watershed/country a commune's survey-backed values are inherited from. */
  inherited_from: { id: string; name: string } | null;
  underlying: IntelUnderlying[];
  /**
   * UC2 launch (backend task-1 report §4.3): the keys the engine promotes to the top level
   * of a `uc2` detail. All optional — a `uc1`/`uc3` detail carries none of them, a
   * not-assessed watershed carries no `similar`, and a country row carries `counts`/
   * `n_watersheds`/`positive_area_ha` instead of the per-watershed numbers.
   */
  reason?: string | null;
  series?: {
    cropland?: IntelSeriesPoint[];
    veg?: IntelSeriesPoint[];
    rain?: IntelSeriesPoint[];
  };
  rain_mean?: number | null;
  rain_tendency?: string | null;
  cropland_delta_pct?: number | null;
  /** The same three readings the units row carries, for the focused unit's own block. */
  change_drivers?: IntelUc2ChangeDrivers;
  area_ha?: number | null;
  adoption?: Record<string, number | null>;
  programme_means?: Record<string, number | null>;
  greening?: { completeness: number | null; gated: boolean; threshold: number | null };
  /** Per practice group, the precomputed top-3 — so the practice selector costs no request. */
  similar?: Record<string, IntelSimilarRow[]>;
  /** Country rows only. */
  counts?: Record<string, number>;
  n_watersheds?: number;
  positive_area_ha?: number | null;
}

export const fetchIntelUnitDetail = (
  useCase: string,
  unitId: string,
  lang: Language = currentLanguage(),
) =>
  getJson<IntelUnitDetail>(
    `/intelligence/${encodeURIComponent(useCase)}/units/${encodeURIComponent(unitId)}${langParam(lang)}`,
  );

/** `GET {uc}/methodology` — design §7, generated from the registry/config/run. */
export interface IntelMethodology {
  use_case: string;
  methodology_key: string;
  approach: string;
  datasets: { layer: string; dataset: string; version: string; catalogue_url: string }[];
  indicators: { level: string; period: number | string; indicator: string }[];
  config: Record<string, unknown>;
  config_version: string;
  last_run: IntelRunRef | null;
  /** The definition's numbered steps, already in the request language; `[]` when absent. */
  steps?: string[];
  /** The definition's limitations paragraph in the request language; `null` when absent. */
  limitations_text?: string | null;
  /**
   * The run's own tercile boundaries: `{dimension: {metric: {country: [b1, b2]}}}`, with
   * the string `"programme"` in place of the pair for a country whose watersheds were too
   * few and which therefore used the programme-wide boundaries.
   */
  terciles?: Record<string, Record<string, Record<string, [number, number] | 'programme'>>>;
  /** The programme-wide boundaries each `"programme"` marker above points at. */
  programme_terciles?: Record<string, [number, number]>;
}

export const fetchIntelMethodology = (useCase: string, lang: Language = currentLanguage()) =>
  getJson<IntelMethodology>(
    `/intelligence/${encodeURIComponent(useCase)}/methodology${langParam(lang)}`,
  );

/** `{uc}/config` — a flat `{key: value}` object; values are whatever JSON type the seed is. */
export type IntelConfig = Record<string, unknown>;

export const fetchIntelConfig = (useCase: string) =>
  getJson<IntelConfig>(`/intelligence/${encodeURIComponent(useCase)}/config`);

/**
 * `PATCH {uc}/config {key: value, …}` → `{config_version}`. A type mismatch on any key is a
 * 422 the caller renders inline (`ApiError.details` carries `{key, expected, got}`); nothing
 * is written when one key of several is bad (design §1.4/backend Task 1 fix round 1).
 */
export const patchIntelConfig = (useCase: string, patch: IntelConfig) =>
  sendJson<{ config_version: string }>(
    `/intelligence/${encodeURIComponent(useCase)}/config`,
    patch,
    'PATCH',
  ).then((r) => r as { config_version: string });

/** `POST {uc}/recompute` → a real, pollable, `queued` `IntelRun` id. */
export const recomputeIntel = (useCase: string) =>
  sendJson<{ run_id: string; status: string }>(
    `/intelligence/${encodeURIComponent(useCase)}/recompute`,
    undefined,
  ).then((r) => r as { run_id: string; status: string });

/* ------------------------------------------------------ Task 7: UC2/UC3 own reads */

/**
 * `GET uc2/similar/{unit_id}?k=&group=` — Amendment 1 §3.2's "Find comparable landscapes",
 * watershed-to-watershed only. `inherited_from` is always `null` now (§0: no commune level
 * anywhere), kept typed only because the endpoint still answers the field.
 */
export interface IntelSimilarReference {
  unit_id: string;
  name: string;
  adoption: Record<string, number>;
  inherited_from: { id: string; name: string } | null;
}

export interface IntelSimilarCandidate {
  unit_id: string;
  name: string;
  distance: number;
  adoption: Record<string, number>;
  opportunity: boolean;
  /** The definition's own exact `templates.opportunity` text, set only when `opportunity`. */
  label: { en: string; fr: string } | null;
  inherited_from: { id: string; name: string } | null;
}

export interface IntelSimilarResult {
  reference: IntelSimilarReference;
  comparable: IntelSimilarCandidate[];
}

export const fetchIntelSimilar = (
  useCase: string,
  unitId: string,
  k: number = 10,
  group: string | null = null,
) =>
  getJson<IntelSimilarResult>(
    `/intelligence/${encodeURIComponent(useCase)}/similar/${encodeURIComponent(unitId)}${qs({ k, group })}`,
  );

/** `GET uc2/compare?a=&b=` — the two units' `{uc}/units/{id}` rows side by side. */
export interface IntelCompareResult {
  a: IntelUnitDetail;
  b: IntelUnitDetail;
  /** The `drivers[].dimension` ids both rows share, in the order the pane prints them. */
  fields: string[];
}

export const fetchIntelCompare = (useCase: string, a: string, b: string, lang: Language = currentLanguage()) =>
  getJson<IntelCompareResult>(
    `/intelligence/${encodeURIComponent(useCase)}/compare${qs({ a, b })}${langParam(lang)}`,
  );

/**
 * `GET uc3/diagnosis?crop=&country=&year=` — design §4 step 9's ranked results source.
 *
 * Amendment 1 §3.3: no correlation coefficient anywhere in UC3 — the old `points`/
 * `correlations` (Spearman ρ) shape is replaced outright by a pre-ranked list (watersheds
 * ordered by yield, each row carrying its own NDVI/rainfall/SOC alongside) and server-built
 * rank-based `statements` ("the three lowest-yield watersheds are also in the bottom half
 * for season NDVI"), read exactly like `values.sentences` elsewhere in this feature.
 */
export interface IntelDiagnosisRankedRow {
  rank: number;
  unit_id: string;
  name: string;
  country: string;
  yield: number | null;
  /** GAEZ round: the crop's modelled yield (t/ha, FAO GAEZ v5, 2020) — `null` while a crop's own layer has no published version yet. */
  modelled_yield: number | null;
  /** NOTES #753: the crop's modelled 2020 production inside the unit (t, FAO GAEZ v5 zonal sum). */
  production_sum?: number | null;
  ndvi_peak: number | null;
  rain: number | null;
  soc: number | null;
}

/** Fix round 1: `countries[]` — the same four columns, one row per country, no `rank`. */
export interface IntelDiagnosisCountryRow {
  unit_id: string;
  name: string;
  yield: number | null;
  modelled_yield: number | null;
  /** NOTES #753: the crop's modelled 2020 production inside the unit (t, FAO GAEZ v5 zonal sum). */
  production_sum?: number | null;
  ndvi_peak: number | null;
  rain: number | null;
  soc: number | null;
}

export interface IntelDiagnosis {
  crop: string;
  year: number;
  season: string;
  /**
   * Fix round 1: a semantic word (`"annual"`/`"dekad"`), not a layer id — confirmed against
   * the live payload (`GET uc3/diagnosis?crop=maize&year=2026` answered `"annual"`), not the
   * `chirps`/`chirpsy` dataset ids this doc comment originally guessed.
   */
  rainfall_source: string;
  /**
   * Part C (11 Oct 2026): the unit each served factor is in, from the source layers'
   * display contracts (`ndvi_peak` "NDVI", `rain` "mm", `soc` "g/kg", ...). The values in
   * `ranked`/`countries` are already converted. Optional: older payloads.
   */
  units?: Partial<Record<'ndvi_peak' | 'rain' | 'soc' | 'modelled_yield' | 'production_sum', string>>;
  ranked: IntelDiagnosisRankedRow[];
  countries: IntelDiagnosisCountryRow[];
  /**
   * NOTES #753: every watershed's and country's modelled 2020 production of the crop, the
   * zonal sum of `gaez_prd_<crop>` in tonnes, keyed by unit id (a watershed id or a country's
   * admin-0 id). `sums` is empty when the raster is not published. Optional: older payloads.
   */
  production?: {
    layer: string;
    /** NOTES #759: `ggcp10` (the crop production grid) or `gaez` (sorghum, millet). */
    source?: 'ggcp10' | 'gaez';
    /** The production year read (2020 for a later chosen year). */
    year: number;
    /** "Production is for 2020, the grid's last year." when the year was moved. */
    note?: { en: string; fr: string } | null;
    unit: string;
    sums: Record<string, number>;
  };
  /** NOTES #759: the GAEZ v5 2020 total, for the Productivity step's Modelled measure. */
  production_modelled?: { layer: string; source?: string; year: number; unit: string; sums: Record<string, number> };
  /** NOTES #759: the NDVI season the vegetation column reads, and its note when moved. */
  vegetation?: { year: number; note: { en: string; fr: string } | null };
  statements: { en: string[]; fr: string[] };
  insight: { en: string; fr: string };
  limitations: { en: string; fr: string }[];
}

export const fetchIntelDiagnosis = (crop: string, year: number, country: Country | null = null) =>
  getJson<IntelDiagnosis>(
    `/intelligence/uc3/diagnosis${qs({ crop, year, country: country && country !== 'ALL' ? country : null })}`,
  );

/** `GET uc3/series?unit=&year=` — the NDVI/rain time series for the selected unit. */
export interface IntelSeries {
  unit: string;
  year: number;
  season: string;
  ndvi: { date: string; mean: number }[];
  rain: { month: string; total: number }[];
  /** Part C: the units the series are served in (NDVI in true units). Optional: older payloads. */
  units?: { ndvi: string; rain: string; aeti: string };
}

export const fetchIntelSeries = (unit: string, year: number) =>
  getJson<IntelSeries>(`/intelligence/uc3/series${qs({ unit, year })}`);

/** `GET uc3/config` — crops, the cropping calendar and the two scenario templates. */
export interface IntelConditionClause {
  layer: string;
  op: 'lt' | 'gt' | 'between' | 'class_in' | 'tercile';
  value: unknown;
}

/**
 * GAEZ round: one `crops[]` entry — `id` is what the pane sends as `?crop=`/`crop` and what
 * `mask:<c>`/`yield:<c>`/`gaez:<c>`/`gaez_gap:<c>` clause aliases are built from; `mask` is
 * `null` for a crop with no BACS mask (sorghum, millet), the signal the crop-area rule
 * caveat is shown for.
 */
export interface IntelUc3Crop {
  id: string;
  label: { en: string; fr: string };
  survey: string;
  mask: string | null;
  gaez: string;
}

export interface IntelUc3Config {
  crops: IntelUc3Crop[];
  calendar: Record<string, [number, number]>;
  survey_levels: string[];
  scenarios: Record<'A' | 'B', { clauses: IntelConditionClause[] }>;
  /** The crop-area rule's own two sentences — `gaez_fallback` is the one a mask-less crop needs. */
  crop_area_rule?: { mask: { en: string; fr: string }; gaez_fallback: { en: string; fr: string } };
}

export const fetchIntelUc3Config = () => getJson<IntelUc3Config>('/intelligence/uc3/config');

/**
 * `GET {uc}/definition` — Amendment 1 §2/§3.3: the use case's own stored definition row.
 * UC3's stepper reads `definition.steps` (order, ids, kinds) rather than a hardcoded list,
 * and its rainfall step's `binding.policy`/`binding.datasets` to caption "season dekads" vs
 * "annual total" for whichever of `chirps`/`chirpsy` the archive can actually answer.
 */
export interface IntelStepBinding {
  levels?: string[];
  crops?: Record<string, unknown>;
  indicator?: string;
  layer?: string;
  policy?: string;
  datasets?: Record<string, string>;
  period?: number;
  layers?: string[];
  scenarios?: Record<string, unknown>;
  ranked?: boolean;
  correlations?: boolean;
  /** NOTES #759: the year step's first year (2010, the production grid's first). */
  first?: number;
  /** NOTES #759: the Relationships step's sources (the folded Vegetation and Rainfall steps). */
  ndvi_layer?: string;
  rainfall?: { policy?: string; datasets?: Record<string, string> };
  soil_layers?: string[];
  /** The production grid's years, `[first, last]`. */
  years?: number[];
}

export interface IntelStepDefinitionRow {
  id: string;
  kind:
    | 'geography'
    | 'crop'
    | 'year'
    | 'indicator'
    | 'raster'
    | 'soil_selector'
    | 'relationships'
    | 'conditions'
    | 'results';
  binding: IntelStepBinding;
  optional?: boolean;
}

export interface IntelDefinition {
  id: string;
  status: 'draft' | 'published';
  author: string | null;
  definition_version: number;
  updated_at: string | null;
  definition: {
    steps?: IntelStepDefinitionRow[];
    scenarios?: Record<string, { clauses: IntelConditionClause[] }>;
    caveats?: { en: string; fr: string }[];
    /** GAEZ round: uc3's own five-crop list — kept in sync with `uc3/config`'s own `crops`. */
    crops?: IntelUc3Crop[];
    crop_note?: { en: string; fr: string };
    [key: string]: unknown;
  };
}

export const fetchIntelDefinition = (useCase: string) =>
  getJson<IntelDefinition>(`/intelligence/${encodeURIComponent(useCase)}/definition`);

/**
 * Amendment 1 §1/§A.3: `GET intelligence/help` — one row per `help_cards` seed, in `order`.
 * `target` is the "Take me there" deep link; `tab` is always present, the rest are the
 * kind-specific fields a card's target can carry (a use case, a step inside it, a tool
 * under My Analysis, or the `draw_aoi` action).
 */
export interface IntelHelpTarget {
  tab: 'usecases' | 'tools' | 'help';
  use_case?: string;
  step?: number;
  tool?: string;
  action?: 'draw_aoi';
}

export interface IntelHelpCard {
  id: string;
  title: string;
  /** An `IconName` key server-side too (`lib/icons.tsx`'s own set); unknown names fall back. */
  icon: string;
  steps: string[];
  target: IntelHelpTarget;
  order: number;
}

/** Title and steps are translated server-side (`?lang=`), like `fetchIntelUseCases`. */
export const fetchIntelHelp = (lang: Language = currentLanguage()) =>
  getJson<IntelHelpCard[]>(`/intelligence/help${langParam(lang)}`);

/* ---- §D4 datasets */

/** §D4 `data_type` — `apps.datasets.catalogue.DATA_TYPES`. */
export type DatasetDataType = 'raster' | 'vector' | 'tabular';

/** §D4 `classification` — `apps.datasets.catalogue.CLASSIFICATIONS`. */
export type DatasetClassification = 'open' | 'sensitive' | 'restricted';

/** §D4 `latest_version` — `null` for a dataset with no published version yet. */
export interface DatasetLatestVersion {
  version: string;
  published_at: string | null;
}

/**
 * §F9 `preview` — where "Preview on map" should land, decided by the server.
 *
 * `apps.datasets.catalogue.preview_for`. Three answers and a `null`: a row with a map layer
 * opens that layer on its dataset's own theme; `mels_values` opens the Indicators theme; a
 * `bl_<sheet>` row opens that sheet's baseline theme; anything else has nowhere to go and
 * says so, so the card keeps its toast rather than navigating to an unchanged map.
 *
 * `theme` is a **theme key** (`domFilter`), not a permalink key — the preview selects it
 * through the store the way the rail's chip does, which is why nothing here is a hash.
 */
export interface DatasetPreview {
  kind: 'layer' | 'indicators' | 'baseline';
  layer_id: string | null;
  theme: string | null;
}

/**
 * §D4 `GET /datasets` row — `apps.datasets.catalogue.dataset_row`.
 *
 * Every row is listed regardless of the caller's §I1 access: `access` is the role's own
 * cell (`full|limited|none`) so the card can print the lock the mockup prints, not a
 * filter that hides the row outright — see that module's docstring.
 */
export interface DatasetRow {
  id: string;
  title: string;
  abstract: string;
  domain: string | null;
  source_org: string;
  data_type: DatasetDataType;
  format: string;
  classification: DatasetClassification;
  is_provisional: boolean;
  latest_version: DatasetLatestVersion | null;
  updated_at: string;
  access: AccessLevel;
  layer_ids: string[];
  /**
   * §F9 (client item 9): the one layer that stands for this dataset —
   * `apps.datasets.catalogue.primary_layer`, the same choice the card's thumbnail is drawn
   * from. `layer_ids[0]` is primary key order and was the wrong answer for any dataset
   * carrying more than one layer.
   */
  primary_layer_id: string | null;
  preview: DatasetPreview | null;
  /**
   * §2: this dataset has a rows provider, so `GET /datasets/{id}/rows` will answer and the
   * record's Data tab is worth drawing. Sent on the list row as well as the record so the
   * catalogue never has to probe the endpoint to find out.
   */
  has_rows: boolean;
  /**
   * The provenance four (client, 2026-09-22): *"we should capture the data source as well,
   * how it was captured and accuracy information; for rasters, it is good to also capture
   * resolution information."*
   *
   * On the **row** rather than only on the record because the catalogue card and the
   * manager's list both read from the row, and a field that only `GET /datasets/{id}`
   * carries cannot be shown until the drawer is open. Every one is optional: nothing in the
   * catalogue today has them, and a dataset without them stays publishable.
   */
  /** Where the data came from — the upstream dataset, sensor, survey or web address. */
  source_detail: string | null;
  /** How it was captured — field survey, remote sensing, model output, digitised map. */
  capture_method: string | null;
  /** Positional or thematic accuracy, the validation done, the known limitations. */
  accuracy: string | null;
  /** The pixel size or map scale — `250 m`, `10 m`, `1:50 000`. Measured for rasters. */
  resolution: string | null;
}

/** §D4 `facets.domain[]` — counts over the whole catalogue (`apps.datasets.catalogue.facets`). */
export interface DatasetDomainFacet {
  id: string;
  name: string;
  color: string;
  count: number;
}

/** §D4 `facets.data_type[]` / `facets.classification[]` / `facets.source[]`. */
export interface DatasetFacetCount {
  id: string;
  count: number;
  /**
   * The option's own printable name, where it has one that is not its id.
   *
   * `data_type`/`classification` are closed vocabularies whose labels are translated on this
   * side (`catalogue.type.*`/`catalogue.cls.*`), so those rows carry no `label`. `source` is
   * the free-text `Dataset.source_org` — an organisation, not a concept — so the server sends
   * `label === id` and the rail prints it as it stands, in either language.
   */
  label?: string;
}

export interface DatasetFacets {
  domain: DatasetDomainFacet[];
  data_type: DatasetFacetCount[];
  classification: DatasetFacetCount[];
  /**
   * Client, 2026-09-16: *"check also by Data Source — MEL, Baseline Upload, ISRIC etc."*
   * Every distinct `source_org` in the catalogue, sorted by label, counted over the whole
   * catalogue like every other facet here.
   */
  source: DatasetFacetCount[];
}

/**
 * §D4 `GET /datasets` envelope. `facets` is always computed over the whole catalogue —
 * `total`, not `count`, is what it describes — so the §F8 rail's counts never move as the
 * caller narrows `results` client-side (see `catalogue.applyFilters`).
 */
export interface DatasetsResponse {
  count: number;
  total: number;
  facets: DatasetFacets;
  results: DatasetRow[];
}

/** §D4 `versions[]` — `apps.datasets.catalogue.record`, newest first. */
export interface DatasetVersionEntry {
  version: string;
  published_at: string | null;
  change_note: string | null;
  size_bytes: number | null;
  published_by: string | null;
}

/** §D4 `GET /datasets/{id}` — the list row plus lineage, licence and versions. */
export interface DatasetRecord extends DatasetRow {
  crs: string;
  coverage: string | null;
  licence: string | null;
  cadence: string | null;
  lineage: string | null;
  contact: string | null;
  website_visible: boolean;
  versions: DatasetVersionEntry[];
  api_snippet: string;
}

export type DatasetsKey = readonly ['datasets', Language, string | null];

/**
 * §D4 `GET /datasets[?lang=fr][&source=…]` — the catalogue.
 *
 * `source` is the one axis the server narrows on the wire (exact match on `source_org`);
 * `q`/`domain`/`data_type`/`classification` stay client-side over the one payload. §F8 sends
 * it whenever exactly one organisation is ticked in the "Data source" rail, and sends
 * nothing when none or several are — `?source=` is single-valued, so two ticked
 * organisations mean the whole catalogue plus a client-side OR (`catalogue.applyFilters`).
 * Either way the envelope's `facets` describe the whole catalogue, never the narrowed read.
 */
export const fetchDatasets: QueryFunction<DatasetsResponse, DatasetsKey> = ({
  queryKey: [, lang, source],
}) =>
  getJson<DatasetsResponse>(
    // `lang=en` and a null `source` both stay off the URL (`qs` drops them), so the
    // English whole-catalogue read is byte-for-byte the request it has always been.
    `/datasets${qs({ lang: lang === DEFAULT_LANGUAGE ? null : lang, source })}`,
  );

export type DatasetKey = readonly ['dataset', string, Language];

/** §D4 `GET /datasets/{id}[?lang=fr]` — the §F8 detail drawer. */
export const fetchDataset: QueryFunction<DatasetRecord, DatasetKey> = ({
  queryKey: [, id, lang],
}) => getJson<DatasetRecord>(`/datasets/${encodeURIComponent(id)}${langParam(lang)}`);

/** §D4 `GET /datasets/{id}/download[?version=]` — a 302 to a signed, time-limited URL. */
export function datasetDownloadUrl(id: string, version?: string | null): string {
  return `${API_BASE}/datasets/${encodeURIComponent(id)}/download${qs({ version: version ?? null })}`;
}

/**
 * §F8 card thumbnail (client, 2026-09-15) — one URL per dataset. A raster answers with a
 * 302 to TiTiler's styled preview, an API vector layer with a PNG drawn on the server, and
 * anything else 404s, which the card turns into its data-type icon (`DatasetThumb`).
 */
export function datasetThumbnailUrl(id: string): string {
  return `${API_BASE}/datasets/${encodeURIComponent(id)}/thumbnail.png`;
}

/* ---- M6 §4 management (`features/manager/DatasetsTab.tsx`) */

/**
 * M6 §4 — a catalogue row as the **Data Manager** sees it.
 *
 * `GET /datasets?include_hidden=1` answers the same rows the public catalogue gets plus the
 * three columns only a manager may act on: whether the dataset is listed publicly, how many
 * versions it carries, and whether `DELETE` would be refused. The last two are optional
 * because they are a manager-only enrichment: a server that has not shipped them yet leaves
 * the table printing an em dash rather than a wrong count, and `deletable` defaults to
 * "offer it and let the 409 explain" rather than hiding a button that might well work.
 */
export interface ManagedDatasetRow extends DatasetRow {
  website_visible: boolean;
  versions_count?: number;
  deletable?: boolean;
}

/** `GET /datasets?include_hidden=1` — the catalogue envelope over `ManagedDatasetRow`s. */
export interface ManagedDatasetsResponse extends Omit<DatasetsResponse, 'results'> {
  results: ManagedDatasetRow[];
}

export type DatasetsAllKey = readonly ['datasetsAll', Language];

/**
 * M6 §4 `GET /datasets?include_hidden=1` — every dataset, unpublished ones included.
 *
 * A separate key from `queryKeys.datasets()` on purpose: the two reads are different sets of
 * rows over one URL, and letting the manager's answer land in the catalogue's cache entry
 * would put unpublished datasets on the public §F8 page. `credentials` are sent because
 * `include_hidden` is a permission, not a parameter.
 */
export const fetchDatasetsAll: QueryFunction<ManagedDatasetsResponse, DatasetsAllKey> = ({
  queryKey: [, lang],
}) =>
  getJson<ManagedDatasetsResponse>(
    `/datasets${qs({ include_hidden: 1, lang: lang === DEFAULT_LANGUAGE ? null : lang })}`,
    { credentials: 'same-origin' },
  );

/** M6 §4 `PATCH /datasets/{id}` — the editable half of the catalogue record. */
export interface DatasetPatch {
  title: string;
  abstract: string;
  domain: string | null;
  source_org: string;
  licence: string | null;
  cadence: string | null;
  lineage: string | null;
  contact: string | null;
  source_detail: string | null;
  capture_method: string | null;
  accuracy: string | null;
  resolution: string | null;
  classification: DatasetClassification;
  website_visible: boolean;
}

/** M6 §4 `PATCH /datasets/{id}` → the updated record. */
export const patchDataset = (id: string, fields: Partial<DatasetPatch>) =>
  sendJson<DatasetRecord>(`/datasets/${encodeURIComponent(id)}`, fields, 'PATCH').then(
    (row) => row as DatasetRecord,
  );

/**
 * M6 §4 `POST /datasets/{id}/unpublish|republish` → the updated record.
 *
 * Two routes rather than a `PATCH {website_visible}` because they are two audited acts on the
 * server (the layer, the thumbnails and the catalogue caches all move), and the caller says
 * which one it means by the boolean it already has in hand.
 */
export const setDatasetVisibility = (id: string, visible: boolean) =>
  sendJson<DatasetRecord>(
    `/datasets/${encodeURIComponent(id)}/${visible ? 'republish' : 'unpublish'}`,
    {},
  ).then((row) => row as DatasetRecord);

/**
 * M6 §4 `DELETE /datasets/{id}` `{confirm}` — 204, or 409 `not_deletable` with a reason.
 *
 * `confirm` is the dataset id typed back by the reader; the server checks it too, so the
 * dialog's gate is a courtesy rather than the protection.
 */
export const deleteDataset = (id: string, confirm: string) =>
  sendJson<null>(`/datasets/${encodeURIComponent(id)}`, { confirm }, 'DELETE').then(
    () => undefined,
  );

/* ---- §2 tabular rows in the dataset record (`apps.datasets.rows`) */

/** One column of `GET /datasets/{id}/rows` — `kind` decides the cell's alignment. */
export interface DatasetRowsColumn {
  key: string;
  /** Already translated server-side (`RowColumn.payload`), so it is printed as it arrives. */
  label: string;
  kind: 'text' | 'number';
}

/** A single data row: the column keys of this dataset's provider, nothing else. */
export type DatasetRowsRow = Record<string, string | number | boolean | null>;

/** §2 `GET /datasets/{id}/rows` envelope. */
export interface DatasetRowsResponse {
  count: number;
  page: number;
  page_size: number;
  columns: DatasetRowsColumn[];
  rows: DatasetRowsRow[];
}

export type RowsSortDir = 'asc' | 'desc';

/**
 * The read the Data tab is looking at: one page, one search term, one ordering.
 *
 * `sort: null` means "the provider's own order" — the server's default — and is what keeps
 * the first request of a freshly opened tab the bare `?page=1&page_size=50` it should be.
 */
export interface DatasetRowsParams {
  page: number;
  pageSize: number;
  q: string;
  sort: string | null;
  dir: RowsSortDir;
}

/**
 * The params carried as one object member rather than five tuple slots: the key is read
 * back by `fetchDatasetRows` below, and five anonymous positions in a row (two of them
 * strings that can both be empty) is the kind of key nobody can check at a glance.
 * TanStack hashes objects by sorted keys, so this is as stable an entry as a tuple.
 */
export type DatasetRowsKey = readonly ['datasetRows', string, DatasetRowsParams, Language];

/** The query half of a rows URL, shared by the JSON read and the CSV href. */
function rowsQuery(
  params: Partial<DatasetRowsParams>,
  lang: Language,
  extra: Record<string, string | number | null> = {},
): string {
  // `qs` drops empty strings and nulls, so an unsorted, unsearched, English page 1 asks for
  // `?page=1&page_size=50` and nothing else.
  return qs({
    page: params.page ?? null,
    page_size: params.pageSize ?? null,
    q: params.q ?? null,
    sort: params.sort ?? null,
    // A direction without a column is noise: the server ignores it, and it would split the
    // cache entry for two identical reads.
    dir: params.sort ? (params.dir ?? null) : null,
    lang: lang === DEFAULT_LANGUAGE ? null : lang,
    ...extra,
  });
}

/**
 * §2 `GET /datasets/{id}/rows` — the record's Data tab.
 *
 * A `QueryFunction` reading the key it was invoked under, for the reason the localised
 * catalogue fetchers above give: the column *labels* are translated server-side, so the
 * language belongs on the key, and taking it off the key rather than re-reading
 * `currentLanguage()` makes it impossible for the request and its cache entry to disagree.
 */
export const fetchDatasetRows: QueryFunction<DatasetRowsResponse, DatasetRowsKey> = ({
  queryKey: [, id, params, lang],
}) => getJson<DatasetRowsResponse>(`/datasets/${encodeURIComponent(id)}/rows${rowsQuery(params, lang)}`);

/**
 * §2 `GET /datasets/{id}/rows?format=csv` — the Data tab's "Export CSV" href.
 *
 * The search and the ordering travel; the paging deliberately does not, because the server
 * streams **the whole filtered set** under `format=csv` and exporting page 3 of a table the
 * reader has just searched would be a surprise.
 */
export function datasetRowsCsvUrl(
  id: string,
  params: Pick<Partial<DatasetRowsParams>, 'q' | 'sort' | 'dir'> = {},
  lang: Language = currentLanguage(),
): string {
  return `${API_BASE}/datasets/${encodeURIComponent(id)}/rows${rowsQuery(
    { q: params.q, sort: params.sort, dir: params.dir },
    lang,
    { format: 'csv' },
  )}`;
}

/* ---- Data Manager round 2 (client, 2026-09-18): editing a dataset's rows in place */

/**
 * One column of `GET /datasets/{id}/rows/all` — the editable grid's header.
 *
 * Wider than `DatasetRowsColumn`: the grid types its inputs (`date` as well as text and
 * number) and has to know which columns the server will refuse a value for — a vector's
 * geometry column, an id the provider owns — so `editable` travels with the label rather
 * than being guessed on this side.
 */
export interface DatasetRowsAllColumn {
  key: string;
  /** Already translated server-side, like the paged read's label. */
  label: string;
  kind: 'text' | 'number' | 'date';
  editable: boolean;
}

/** A grid row: the dataset's own keys plus the server's stable row id. */
export type DatasetRowsAllRow = { _id: string | number } & Record<
  string,
  string | number | boolean | null
>;

/**
 * `GET /datasets/{id}/rows/all` — every row of a vector or tabular dataset, in one read.
 *
 * Unpaged on purpose: the grid edits a *set*, and an edit on page 3 of a paged read would
 * be posted against rows the client cannot see. `cap` is the server's ceiling and `count`
 * what it actually sent, so a dataset over the cap can be named as such rather than being
 * silently truncated; `version` is the version the rows came from, which the save carries
 * back so a concurrent publish is a conflict rather than an overwrite.
 */
export interface DatasetRowsAllResponse {
  columns: DatasetRowsAllColumn[];
  rows: DatasetRowsAllRow[];
  count: number;
  cap: number;
  version: string | null;
  /** Where the rows live (`file`, a provider name) — the grid greys what it cannot edit. */
  provider?: string | null;
}

export type DatasetRowsAllKey = readonly ['datasetRowsAll', string, Language];

/**
 * The whole-table read. Raster datasets answer 404 — there are no rows to edit — and the
 * panel says so rather than showing an empty grid.
 */
export const fetchDatasetRowsAll: QueryFunction<DatasetRowsAllResponse, DatasetRowsAllKey> = ({
  queryKey: [, id, lang],
}) =>
  getJson<DatasetRowsAllResponse>(
    `/datasets/${encodeURIComponent(id)}/rows/all${langParam(lang)}`,
    { credentials: 'same-origin' },
  );

/** The body of `POST /datasets/{id}/versions` — the edited set, whole. */
export interface DatasetVersionBody {
  change_note: string;
  /**
   * The version the rows were read from (client, 2026-09-18 review).
   *
   * The save replaces the whole set, so a version published while the grid was open would be
   * silently overwritten by a copy of the older one. The server compares this against the
   * dataset's current version and refuses the save as a `conflict` 422 instead.
   */
  base_version?: string | null;
  /** The column keys the rows are written under, in order. */
  columns: string[];
  /** A row without `_id` is an insert; an omitted `_id` that was read is a delete. */
  rows: Array<Record<string, string | number | boolean | null | undefined>>;
}

/** What the server answers with — enough to name the version in the toast. */
export interface DatasetVersionCreated {
  version: string;
  count?: number;
  dataset_id?: string;
}

/**
 * `POST /datasets/{id}/versions` — publish the edited rows as a new version.
 *
 * Vectors take edits and deletes only (an insert has no geometry and is refused 422
 * `no_geometry`); tables take all three. A cell the server cannot coerce comes back as a
 * 422 naming that cell, which the grid prints against the cell rather than as a banner.
 */
export const createDatasetVersion = (id: string, body: DatasetVersionBody) =>
  sendJson<DatasetVersionCreated>(
    `/datasets/${encodeURIComponent(id)}/versions`,
    body as unknown as Record<string, unknown>,
  ).then((row) => row as DatasetVersionCreated);

/* ---- §F7 dashboard */

/** §F7 `kpis[]` — the KPI rail (`apps.meta.dashboard.KPI_IDS`, `build_dashboard`). */
export interface DashboardKpi {
  id: string;
  code: string | null;
  label: string;
  unit: string;
  domain: string | null;
  /**
   * Whether the indicator has real (non-demo) approved values — `provenance.is_active`, the
   * same rule the Indicators pane and the map already grey an indicator out by
   * (`apps.meta.dashboard._active_map`, backend `3529d6c`).
   *
   * When `false` the server sends `value`/`previous`/`delta` as `null` and `spark` as six
   * `null`s: a demo-only indicator is *unmeasured*, not achieved at zero, and the tile stays
   * on the rail saying so rather than printing `seed_demo`'s simulated totals.
   */
  active: boolean;
  value: number | null;
  previous: number | null;
  delta: number | null;
  spark: (number | null)[];
  fmt: number;
  target: { cumulative_target: number; pct: number } | null;
  /**
   * §F7 §1 (client, 2026-09-16) — which numbered section of the dashboard the indicator
   * belongs to: `contractual` (the grant agreement's own committed indicators),
   * `outcome` (the results-framework indicators measuring change on the ground) or
   * `other` (everything else the payload carries).
   *
   * **Optional on the type though the server sends it.** `/dashboard` is cached in the
   * browser like every other §D payload, so a reader can hold a pre-tier response from
   * before this field existed — and the page must section that payload rather than crash
   * over one missing string. `features/dashboard/dashboard.ts`'s `tierOf` falls back to the
   * indicator's own code when it is absent.
   */
  tier?: 'contractual' | 'outcome' | 'other';
  /** The org unit every figure on this tile was read for — `null` at programme/country scope. */
  unit_id?: string | null;
}

/** §F7 `targets[]` — the target-vs-achievement table and its derived `status`. */
export interface DashboardTargetRow {
  id: string;
  code: string;
  label: string;
  unit: string;
  achieved: number | null;
  target: number | null;
  pct: number | null;
  status: 'measured' | 'awaiting' | 'no_target';
  /** `trajectory` for an IFDC row, `actuals` when the year's actual stands as its target. */
  target_source: 'trajectory' | 'actuals' | 'apportioned' | 'allocated' | null;
  /** See `DashboardKpi.tier` — the same three values, on the same payload. */
  tier?: 'contractual' | 'outcome' | 'other';
  /** See `DashboardKpi.unit_id` — the same scoped unit, on the same payload. */
  unit_id?: string | null;
}

/** §F7 `insights[]` — the target sentences, keyed for translation-stable testing. */
export interface DashboardInsight {
  key: string;
  text: string;
}

/** §F7 `trend` — B.2.1 across the scope's countries. */
export interface DashboardTrend {
  indicator: string;
  label: string;
  unit: string;
  fmt: number;
  years: number[];
  series: { country: string; name: string; values: (number | null)[] }[];
}

/** §F7 `gesi.countries[]` — B.2.1's female/youth share, per country. */
export interface DashboardGesiCountry {
  country: string;
  name: string;
  b21: number | null;
  women_pct: number | null;
  youth_pct: number | null;
}

export interface DashboardGesi {
  year: number;
  countries: DashboardGesiCountry[];
}

/** §F7 `coverage[]` — one row per domain, datasets/indicators counts and freshness. */
export interface DashboardCoverageRow {
  domain: string;
  name: string;
  color: string;
  datasets: number;
  indicators: number;
  freshness: 'refresh_due' | 'current';
}

/**
 * §F7 `scope.unit` — the `org_units` row the whole payload was read for (backend `76e527c`).
 *
 * It sits *beside* the existing `scope` fields rather than replacing them: `country` is
 * still answered (derived from the unit when one is given), and `unit` is `null` at
 * programme or country scope. `name` is what the page's scope line says out loud;
 * `unit_id`/`unit_type`/`admin_level`/`country` are what "View on map" would need to
 * rebuild a `Scope` if it ever had to work from the payload alone.
 */
export interface DashboardScopeUnit {
  unit_id: string;
  unit_type: string;
  name: string;
  country: string | null;
  admin_level: number | null;
}

/**
 * §1 `baseline` — the Baseline 2025 survey block (backend `apps.meta.dashboard_baseline`).
 *
 * `fmt` is a **decimal count**, not a format name: it is fed to `fmtNum` exactly like
 * `DashboardKpi.fmt` and `DashboardTrend.fmt` are, so one helper formats every number the
 * dashboard prints. `value` and `n` are nullable because a survey series is not measured
 * everywhere — the four `by_country` entries are always listed, `null` and all.
 */
export interface DashboardBaselineCountry {
  iso: string;
  value: number | null;
  n: number | null;
}

/**
 * One watershed's estimate for a series — the `by_watershed` rows behind the baseline
 * filter's "Watersheds" view (client, 2026-09-16).
 *
 * Unlike `by_country` this list is *not* fixed-length: the server answers the watersheds it
 * has an estimate for, at whatever scope the page is on, and the section reads its axis off
 * the payload's own `watersheds` roster rather than counting these.
 */
export interface DashboardBaselineWatershed {
  id: string;
  name: string;
  /** ISO3 of the country the watershed belongs to — the axis groups and colours by it. */
  country: string;
  value: number | null;
  n: number | null;
}

/** A country on the baseline's own roster (`countries`), name included for the axis. */
export interface DashboardBaselineCountryRef {
  iso: string;
  name: string;
}

/** A watershed on the baseline's own roster (`watersheds`). */
export interface DashboardBaselineWatershedRef {
  id: string;
  name: string;
  /** ISO3 of the owning country. */
  country: string;
}

export interface DashboardBaselineSeries {
  id: string;
  name: string;
  unit: string;
  /** Decimals, as `fmtNum` takes them. */
  fmt: number;
  value: number | null;
  n: number | null;
  /** Always the four programme countries, in the server's order. */
  by_country: DashboardBaselineCountry[];
  /**
   * §1 baseline charts — the shape of the series, which is what decides the chart it is
   * drawn as (`features/dashboard/baseline.ts`'s `chartPlan`).
   *
   * `share` is a percentage of households and is drawn against a 0–100 axis; `amount` is a
   * yield, a herd or an income and gets its own scale and a vertical chart. Optional
   * because a cached `/dashboard` payload predates the field — absent reads as `share`,
   * which is 50 of the survey's 58 series.
   */
  kind?: 'share' | 'amount';
  /**
   * The survey question the series belongs to, when several series are one question's
   * answers: `land_tenure` for own/pledged/rented/borrowed, `extension_access` for the
   * women/men pair. `null`/absent = the series stands alone.
   */
  block?: string | null;
  /** Which answer inside `block` this series is (`women`, `rented`, …). */
  sub?: string | null;
  /** The programme-wide estimate, whatever the page's scope — the charts' reference line. */
  programme_value?: number | null;
  /** Per-watershed estimates; absent on a payload from before the filter existed. */
  by_watershed?: DashboardBaselineWatershed[];
  /**
   * §3 — the same series at the nearest **earlier** round of the survey, at this scope.
   *
   * `null` (or absent, on a cached pre-periods payload) means there is nothing to compare
   * against and the card prints no delta: a series first asked in 2027 has no 2025 figure,
   * and a zero change would be a claim the survey never made. `value` is nullable inside it
   * for the same reason it is outside: the round ran, this scope was not measured.
   */
  previous?: { period: number; value: number | null; n: number | null } | null;
}

export interface DashboardBaselineSheet {
  group_id: string;
  label: string;
  series: DashboardBaselineSeries[];
}

export interface DashboardBaseline {
  /**
   * The round these figures are from — **the server's answer, not the page's request**.
   *
   * `?baseline_period=` naming a round the survey never ran is ignored server-side and
   * answered with the latest one (`apps.meta.dashboard_baseline.build`), so the select
   * reads its value back off this field: showing what was asked for would label somebody
   * else's numbers with a year they do not belong to.
   */
  period: number;
  /** Every round the survey has stored, ascending. Absent on a cached pre-periods payload. */
  periods?: number[];
  /**
   * The scope the survey could actually answer for. A region, commune or partnership has no
   * survey estimate, so the server resolves it to that unit's country and sets `fallback`.
   */
  scope_kind: 'programme' | 'country' | 'watershed';
  scope_label: string;
  fallback: boolean;
  /** The scope's largest `sample_n`, `null` when nothing was measured there. */
  households: number | null;
  /**
   * The rosters the §1 filter's "View by" draws its axis from — the four programme
   * countries and every surveyed watershed. Both optional: a cached payload from before the
   * filter has neither, and the section falls back to `COUNTRY_ORDER` for the countries and,
   * for the watersheds, to the `by_watershed` rows the series themselves carry (each of which
   * names its own id, name and country) — `features/dashboard/baseline.ts`'s
   * `watershedRoster`.
   */
  countries?: DashboardBaselineCountryRef[];
  watersheds?: DashboardBaselineWatershedRef[];
  sheets: DashboardBaselineSheet[];
}

/** §F7 `GET /dashboard` — `apps.meta.dashboard.build_dashboard`'s whole payload. */
export interface DashboardResponse {
  scope: {
    country: string | null;
    year: number;
    target_year: number | null;
    domain: string | null;
    /** The scoped org unit (client item 7), `null` when the reader is at country scope. */
    unit: DashboardScopeUnit | null;
  };
  source: { label: string; last_sync: string | null };
  restricted_hidden: boolean;
  kpis: DashboardKpi[];
  targets: DashboardTargetRow[];
  insights: DashboardInsight[];
  trend: DashboardTrend;
  gesi: DashboardGesi;
  coverage: DashboardCoverageRow[];
  /**
   * §1 — the Baseline 2025 survey, ten sheets of `mb_*` series.
   *
   * Optional on the type though the server always sends it: `/dashboard` is cached in the
   * browser like every other §D payload, so a reader can hold a pre-baseline response from
   * before this block existed. `BaselineSection`'s caller treats absence as "nothing to
   * show" rather than crash the rest of the page over one stale field.
   */
  baseline?: DashboardBaseline;
  /*
    The server's `at_a_glance` block is deliberately **not** typed here: the strip it fed was
    cut on 2026-09-16 and nothing on the client reads the field any more. An unlisted key on
    a JSON payload costs nothing; a type nobody uses is a claim the page still shows it.
  */
}

export type DashboardKey = readonly [
  'dashboard',
  Country,
  number,
  string | null,
  /** §F7 `?unit=` — the scoped org unit, `null` at programme or country scope. */
  string | null,
  /** §3 `?baseline_period=` — the survey round, `null` for "whatever the latest is". */
  number | null,
  Language,
];

/**
 * §F7 `GET /dashboard?country=&year=&domain=&unit=&lang=`.
 *
 * **`unit` replaces `country` on the wire, it does not narrow it.** The server derives the
 * country from the unit and ignores a `country=` that disagrees (`apps.meta.views`,
 * backend `e441e7a`), so sending both would make the URL — and therefore the log line and
 * the server cache key — say something the answer does not depend on.
 */
export const fetchDashboard: QueryFunction<DashboardResponse, DashboardKey> = ({
  queryKey: [, country, year, domain, unit, baselinePeriod, lang],
}) =>
  getJson<DashboardResponse>(
    `/dashboard${qs({
      country: unit || country === 'ALL' ? null : country,
      year,
      domain,
      unit,
      baseline_period: baselinePeriod,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/**
 * §F7 `GET /dashboard/export.csv[?country=&domain=&unit=]` — the dashboard data as CSV.
 *
 * `unit` drops `country` for the same reason `fetchDashboard` does: the unit derives it.
 */
export function dashboardCsvUrl(
  country: Country,
  domain?: string | null,
  unit?: string | null,
): string {
  return `${API_BASE}/dashboard/export.csv${qs({
    country: unit || country === 'ALL' ? null : country,
    domain: domain || null,
    unit: unit || null,
  })}`;
}

/** §E8 `POST /analysis/report {year, country?, domain?, unit?}` — queue the §F7 dashboard as a PDF. */
export interface ReportRequest {
  year: number;
  country?: Country;
  domain?: string | null;
  /** §F7 scope (client item 7) — the org unit the report is about; drops `country`. */
  unit?: string | null;
  lang?: Language;
}

/**
 * §E8 `POST /analysis/report[?lang=]` — same §D6 submit envelope `submitExportMap` gets
 * back (`apps.analysis.views.report` mirrors `export_map` minus the layer resolution).
 */
export const submitReport = ({
  country, year, domain, unit, lang = currentLanguage(),
}: ReportRequest) =>
  sendJson<ExportSubmission>(`/analysis/report${langParam(lang)}`, {
    year,
    // The unit derives the country server-side, and `{unit}` and `{unit, country}` are one
    // job there (backend `e441e7a`) — sending both would be two cache keys for one PDF.
    ...(!unit && country && country !== 'ALL' ? { country } : {}),
    ...(domain ? { domain } : {}),
    ...(unit ? { unit } : {}),
  }).then((r) => r as ExportSubmission);

/* -------------------------------------------------------------- §D5 layers */

/**
 * §D5 layer kinds. Exactly one "where the pixels come from" key accompanies each:
 * `tile_template` (`cog_titiler`), `wms` (`wms_external`), `endpoint` (points/lines) —
 * or none at all, when the row is `unavailable` (Task 1 report: a `wms_external` whose
 * `config.proxy` is null or unknown reports itself rather than emitting a broken URL).
 */
export type LayerKind =
  | 'cog_titiler'
  | 'wms_external'
  | 'points_api'
  | 'lines_api'
  /**
   * Wave 4 (docs/DATA_SOURCES.md §3/§6): HydroLAKES shorelines, GRID3 settlement extents and
   * the water-point-density choropleth. Same `endpoint` payload as the other two vector
   * kinds — the addition is a geometry class, not a transport.
   */
  | 'polygons_api';

/** §F2 `Chip(kind)` — the §I1 classification a layer's access is derived from. */
export type Classification = 'open' | 'sensitive' | 'restricted';

/** §D5 legend entry for a categorical raster (`is_categorical`). */
export interface LayerLegendClass {
  label: string;
  color: string;
}

/**
 * §D5 `GET /layers` row.
 *
 * `access` is `access_for(role, classification)` computed **server-side** from the session
 * (§I1), which is why `queryKeys.layers(role)` carries the role: the body is role-dependent
 * and the response is `Cache-Control: private` + `Vary: Cookie` (Task 1 report).
 *
 * NONE-access rows are *omitted* for public/field/partner (§I3), so `access: 'none'` only
 * ever reaches analyst/manager — see `LayersPane`, which still lists such a row (disabled,
 * with the classification on it) because the pane must not depend on that server-side
 * filtering to be safe.
 */
/**
 * Part C (11 Oct 2026): a raster layer's display contract, `shown = stored × scale + offset`,
 * see `lib/display.ts`. `unit` is already in the reader’s language.
 */
export interface RasterDisplay {
  scale: number;
  offset: number;
  unit: string;
  decimals: number;
}

export interface Layer {
  id: string;
  dataset_id: string;
  name: string;
  /**
   * The dataset abstract — the layer's second line, translated with `name` when `?lang=fr`
   * (§F12). Null for a layer with no dataset behind it; §F5.2 still falls back to
   * `{source} · {domain}` when it is absent, which is what it did before this key existed.
   */
  description: string | null;
  domain: string;
  kind: LayerKind;
  classification: Classification;
  is_provisional: boolean;
  is_categorical: boolean;
  /** §G ramp key into `/meta/config.ramps` (`brown`, `lc`, …); null for vector layers. */
  ramp: string | null;
  legend: LayerLegendClass[] | null;
  source_org: string;
  access: AccessLevel;
  /**
   * The CC-BY attribution string a licence obliges the portal to render wherever the pixels
   * are (docs/DATA_SOURCES.md implementation note 4). Null for a layer that needs none.
   */
  attribution: string | null;
  /**
   * §1a rule 4 — a live third-party layer whose pixels are never stored. The proxy already
   * refuses tiles outside the programme bbox; this flag is what tells the map to mask what
   * is left to the programme outline. It also says the layer is **not** analysis-grade.
   */
  display_only: boolean;
  /**
   * The layer's `/features` endpoint is **viewport-scoped**: fetch it with the map bounds as
   * `?bbox=` and re-fetch on pan. Set for the wave-4 feeds, which hold up to ~180 000
   * features against §J1's 2 MB response budget; false for the handful of `sites` rows the
   * mockup shipped with, which are cheaper to fetch once than to re-fetch per pan.
   */
  viewport?: boolean;
  /** `cog_titiler` — a Leaflet URL template; `{z}/{x}/{y}` are substituted verbatim. */
  tile_template?: string;
  /**
   * `wms_external` — the §B3 proxy URL (trailing slash) plus the GetMap parameters.
   *
   * `template` is present instead of `params` for a **RESTful WMTS** upstream (NASA GIBS):
   * the coordinates are in the path, so the client appends it to `url` and hands the result
   * to `L.tileLayer` rather than to `L.tileLayer.wms`. `max_zoom` is the deepest zoom the
   * upstream publishes — past it, a request is a 404 per tile, not a sharper picture.
   */
  wms?: {
    url: string;
    params: Record<string, string>;
    template?: string;
    max_zoom?: number;
  };
  /** `points_api` / `lines_api` / `polygons_api` — the absolute `/features` path. */
  endpoint?: string;
  /** True when the row cannot be drawn at all; `note` says why. */
  unavailable?: boolean;
  note?: string;
  /**
   * §D5 (M4 Task 1): the layer has a **published version with data behind it** — a COG on
   * disk for a raster kind, published features for a vector one. The map does not care (a
   * registered layer with no pixels simply draws nothing), but the §F6 tool pickers do:
   * offering §H3 a raster the engine will refuse turns a form into a failed job.
   */
  has_data?: boolean;
  /**
   * Task 6 §6.4 / design §1.2: a result raster the intelligence backend published for its
   * own pane (`intel_uc1_overlap`, `intel_uc2_ndvi_trend`) — `config.internal` server-side.
   * It stays in this catalogue (tiles, the legend and `setActiveRaster` all still work) but
   * is excluded from the Layers pane's selects and from a theme's dataset list
   * (`layerInTheme`), because the client's "nothing outside the Analysis pane changed"
   * means these rows must not appear as an ordinary dataset a reader stumbles on.
   */
  internal?: boolean;
  /**
   * §D5 (M4 Task 6): the epoch year of a raster that declares one (`config.epoch`), else
   * `null`; absent for the non-raster kinds. **This is where §H5's two epoch selects come
   * from** — `epoch && is_categorical && has_data && access !== 'none'` — which is why
   * there is no `/analysis/epochs` route: one catalogue, already access-filtered.
   */
  epoch?: number | null;
  /**
   * S3's time slider. The **series** half of `epoch`: `epoch` says which year a row *is*,
   * this says which years a series row can step to and which layer id draws each — oldest
   * first, written by the harvester from what is actually on the volume, so the slider can
   * only ever offer a year the platform can draw.
   *
   * Empty on every row but a series one (`lc_annual`), and empty on that one until its
   * first harvest. Deliberately carries **no tile URL per year**: a client takes the year's
   * `layer` back to `/layers` and reads that row's own `tile_template`, which is the path
   * §I1 already gates.
   */
  epochs?: LayerEpoch[];
  /**
   * Task 6: how often a **date series**' epochs recur — `null` for the legacy annual series
   * (`lc_annual`) and for every non-series row. `'8d'` is the MODIS composite cadence
   * (`ndvi_terra`/`ndvi_aqua`), `'dekad'` the Copernicus SWI ten-day cadence, `'month'` the
   * Crop Monitor's monthly synthesis. `epochLabel`'s second argument.
   */
  cadence?: 'annual' | '8d' | 'dekad' | 'month' | null;
  /**
   * A crop-mask row's stencil tile template (`mask_maize`, GEOGLAM-BACS) — same TiTiler
   * `{z}/{x}/{y}` shape as `tile_template`, but rendered with an `expression=`/`colormap=`
   * pair that leaves everything outside the mask transparent. Drawn as an overlay above an
   * NDVI series when the reader picks it as `ndviMask` (`features/map/RasterLayers.tsx`).
   */
  stencil_template?: string;
  /**
   * Part C (11 Oct 2026): the raster display contract, null for a non-raster kind. The
   * legend prints its strip ends through it (`lib/display.ts`); the tiles do not change.
   */
  display?: RasterDisplay | null;
}

/**
 * One epoch of a §D5 series layer's `epochs` — Task 6 widens this to also carry the
 * **dated** series the temporal raster work adds (`ndvi_terra`/`ndvi_aqua`, `swi`,
 * `cm_synth`), on top of the legacy annual `lc_annual` shape this already served.
 *
 * Three concrete shapes reach the client under this one type:
 *
 * * **version series** (`ndvi_terra`) — `date`, `version` (a real string) and
 *   `tile_template` (the COG this exact epoch draws — `RasterLayers` reads it straight,
 *   the way it always read the series row's own `tile_template`).
 * * **proxied series** (`swi`, `cm_synth`) — `date`, `key` (the token the upstream's own
 *   URL names this epoch by — a date for GLAM, `YYYY_MM` for Crop Monitor) and
 *   `version: null` (no COG is held for these; the tile comes from `wms.template` with
 *   `{date}` replaced by `key`). `prelim` marks the newest, still-provisional month.
 * * **legacy annual series** (`lc_annual`) — unchanged, plus a synthesised
 *   `date: "YYYY-01-01"` so every caller that only knows `date` still has one to read.
 */
export interface LayerEpoch {
  year: number;
  /** The §D5 layer id that draws this year. */
  layer: string;
  dataset: string;
  /** `null` for a proxied series (`swi`, `cm_synth`) — no COG is held for those epochs. */
  version: string | null;
  /**
   * ISO `YYYY-MM-DD` — present on every epoch since Task 6 (synthesised as `${year}-01-01`
   * for the legacy annual rows that predate it). `isDateSeries`/`epochDate`/`epochLabel`
   * read this; `year` remains for the legacy year-only display path.
   */
  date?: string;
  /**
   * The upstream's own token for this epoch, for a **proxied** series only — a date for
   * `swi` (`2020-01-11`), `YYYY_MM` for `cm_synth` (`2016_01`). `RasterLayers` substitutes
   * this into `wms.template`'s `{date}` placeholder in place of `date` when it is set,
   * because the two upstreams do not always spell an epoch the same way `date` does.
   */
  key?: string | null;
  /** A version series' own tile template for this exact epoch (`ndvi_terra`). */
  tile_template?: string;
  /**
   * The year is still filling — this version covers January through some month of it, not
   * twelve. Set on `chirpsy`'s current year and **absent** on every finished one, so the
   * payload says nothing about the years a reader has no caveat to hear about.
   *
   * Written from the published version's own change note rather than from today's date
   * (`apps.datasets.derive.rainfall.TO_DATE_MARKER`): a `v2026` published last December is
   * complete for ever, whatever month it is now.
   */
  to_date?: boolean;
  /** A proxied series' newest epoch, still subject to revision upstream (`cm_synth`). */
  prelim?: boolean;
}

/** §D5 `GET /layers[?lang=fr]` — see `fetchConfig` on why the language is a parameter. */
/**
 * `GET /meta/programme-extent` — the §1a rule-4 display outline (docs/DATA_SOURCES.md).
 *
 * A simplified (0.02°, ~2 km) version of the same four-country dissolve the harvesters cut
 * rasters against, as bare `[lon, lat]` rings. **Not** the cutline: this geometry only ever
 * becomes a CSS `clip-path`, re-projected into pixels on every pan, so it is generalised to
 * ~1 500 points where the real cutline has ~41 000.
 *
 * `available: false` on a stack where `manage.py build_programme_extent` has not run. That
 * is a state, not an error: display-only layers then render bbox-gated but unmasked, which
 * is the same picture with a ragged edge rather than no picture at all.
 */
export interface ProgrammeExtent {
  available: boolean;
  version?: string;
  /** `[west, south, east, north]` in degrees. */
  bbox?: [number, number, number, number];
  /** Outer rings only, each a closed list of `[lon, lat]`. */
  rings?: [number, number][][];
  simplify_deg?: number;
}

export const fetchProgrammeExtent = () => getJson<ProgrammeExtent>('/meta/programme-extent');

export const fetchLayers: QueryFunction<Layer[], LayersKey> = ({ queryKey: [, , lang] }) =>
  getJson<Layer[]>(`/layers${langParam(lang)}`, { credentials: 'same-origin' });

export interface LayerFeatureProperties {
  id?: number | string;
  name?: string;
  site_type?: string;
  /** The middle of the three tooltip lines — water technology, lake type, reach length… */
  category?: string;
  status?: string;
  /**
   * Index into the layer's own `legend`, for a vector layer that is styled per feature
   * (`config.classify`): WPdx' functional status and the water-point-density classes. The
   * server sends a *class*, not a colour, so the swatch in §F5.6 and the mark on the map are
   * the same catalogue row read twice.
   */
  class?: number;
  [key: string]: unknown;
}

/**
 * §D5 `GET /layers/{id}/features?country=`. Layers with nothing published yet answer a
 * valid **empty** FeatureCollection carrying `note` (Task 1 report), so an empty map here
 * is a documented state and never a failed request.
 */
export type LayerFeatures = FeatureCollection<unknown, LayerFeatureProperties> & {
  note?: string;
  access?: AccessLevel;
  /**
   * How many features the filter selected **before** the server's row cap. It differs from
   * `features.length` exactly when the response was truncated, which is the state `note`
   * explains — the wave-4 feeds hold up to ~180 000 features and the endpoint never returns
   * more than a few thousand of them at once.
   */
  matched?: number;
};

/** `minx,miny,maxx,maxy` in EPSG:4326 — the map viewport, as `/features?bbox=` wants it. */
export type FeatureBbox = [number, number, number, number];

export const fetchLayerFeatures = (
  id: string,
  country: Country = 'ALL',
  bbox: FeatureBbox | null = null,
) =>
  getJson<LayerFeatures>(
    `/layers/${encodeURIComponent(id)}/features${qs({
      country: country === 'ALL' ? null : country,
      bbox: bbox ? bbox.map((n) => n.toFixed(4)).join(',') : null,
    })}`,
    { credentials: 'same-origin' },
  );

/* ------------------------------------------------------------- §F1 query keys */

/* ------------------------------------------------- §E8 / §H9 map export (M4 Task 9) */

/** §F5.10's Format select. `csv` is the one that never becomes a job (§E8). */
export type ExportFormat = 'png' | 'pdf' | 'tif' | 'gpkg' | 'csv';

/** §F5.10's Extent select. */
export type ExportExtent = 'view' | 'programme' | 'unit' | 'aoi';

/** Why a switched-on layer is not in the file — `apps.analysis.export.render.WITHHELD_*`. */
export type WithheldReason = 'access' | 'no_data' | 'not_drawable';

export interface ExportWithheld {
  layer: string;
  reason: WithheldReason;
  classification?: string;
}

/**
 * §H9's `state` — the §F1 store, as much of it as a picture of the map needs.
 *
 * `layersOn` is **advisory**: the server decides which of them this role may have at this
 * format and answers with `withheld`. See `apps.analysis.views.export_map`.
 */
export interface ExportState {
  indicator: string | null;
  level: ReportingLevel;
  year: number;
  country: Country;
  scope: string | null;
  layersOn: string[];
  overlays: { id: string; color?: string }[];
  biv: boolean;
  ramp: string | null;
  method: 'quantile' | 'equal';
  k: number;
  /**
   * The drawn ring in **`[lon, lat]`**, GeoJSON's order — which is what
   * `apps.analysis.geometry.ring_of` reads, both to frame `extent: 'aoi'` and to draw the
   * outline on the page. The store keeps Leaflet's `[lat, lon]`, so a caller converts with
   * `lib/analysis.aoiRing` (NOTES #630).
   */
  aoi: [number, number][] | null;
  unit_id: string | null;
  cumulative: boolean;
}

export interface ExportRequest {
  format: Exclude<ExportFormat, 'csv'>;
  extent: ExportExtent;
  /** Required for `extent: 'view'` — `[west, south, east, north]`. */
  bbox?: FeatureBbox;
  state: ExportState;
}

/** The §H9 result payload, once the job is done. */
export interface ExportResult {
  format: string;
  ext: string;
  extent: { kind: string; id: string | null; label: string };
  artifact_uri: string;
  bytes: number;
  detail: Record<string, unknown>;
  layers: { id: string; name: string; kind: string }[];
  withheld: ExportWithheld[];
  omitted_for_role: number;
  notes: string[];
}

/** §D6's answer to the submit, plus §H9's own `withheld` echo. */
export interface ExportSubmission {
  job_id: string;
  status?: 'queued' | 'done' | 'error';
  cached?: boolean;
  result?: ExportResult;
  error?: string;
  withheld: ExportWithheld[];
}

/** §D6 `GET /analysis/jobs/{id}` — the poll every M4 tool lives on. */
export interface AnalysisJobStatus<T = unknown> {
  status: 'queued' | 'running' | 'done' | 'error';
  progress: number;
  result?: T;
  error?: string;
  result_uri?: string;
}

/** §H9 `POST /export/map` — queue a picture (or a package) of the current map. */
export const submitExportMap = (body: ExportRequest) =>
  sendJson<ExportSubmission>('/export/map', body).then((r) => r as ExportSubmission);

/** §D6 `GET /analysis/jobs/{id}`. Not cached: the answer changes between two requests. */
export const fetchAnalysisJob = <T = unknown>(jobId: string) =>
  getJson<AnalysisJobStatus<T>>(`/analysis/jobs/${encodeURIComponent(jobId)}`, {
    credentials: 'same-origin',
  });

/**
 * §D6 `GET /analysis/results/{job_id}.{ext}` — a job's artefact, parsed as JSON.
 *
 * The one caller is the §E7 oversized-result spill (`lib/analysis.ts`): a result over 200 kB
 * is written whole to the job's artefact directory and the row keeps a summary plus this
 * link, so the panel has to follow it to get its rows back.
 *
 * `uri` is the server's own `result_uri`, which is already absolute *and* already carries
 * the `/api/v1` prefix `getJson` would add. Stripping it here rather than re-deriving the
 * path keeps the client from having to know how an artefact is addressed — and a `uri` from
 * anywhere else is passed through unchanged, where it will simply 404 rather than silently
 * become a different endpoint.
 *
 * `same-origin` credentials because the route re-checks §I3 on the job's own inputs: an
 * artefact is exactly as gated as the run that produced it.
 */
export const fetchAnalysisArtifact = <T = unknown>(uri: string) =>
  getJson<T>(uri.startsWith(`${API_BASE}/`) ? uri.slice(API_BASE.length) : uri, {
    credentials: 'same-origin',
  });

/**
 * §D3 `/indicators/export` as a URL — §E8's "CSV: immediate (no job)".
 *
 * A URL rather than a fetch, because the response is a `Content-Disposition: attachment`
 * stream and the browser is better at saving it than we are: the modal renders an ordinary
 * `<a href download>`, so the download survives a slow server, shows in the download
 * manager and needs no blob, no object URL and no revoke.
 */
export function indicatorsExportUrl(params: {
  indicator?: string | null;
  level?: number | null;
  unitId?: string | null;
  year?: number | null;
  country?: Country;
  parent?: string | null;
}): string {
  return `${API_BASE}/indicators/export${qs({
    indicator: params.indicator ?? null,
    unit_id: params.unitId ?? null,
    level: params.unitId ? null : (params.level ?? null),
    year: params.unitId ? null : (params.year ?? null),
    country: params.country && params.country !== 'ALL' ? params.country : null,
    parent: params.unitId ? null : (params.parent ?? null),
    format: 'csv',
  })}`;
}

/* ------------------------------------------------- M6 §1 Data Manager uploads */

/** §1 upload session status. `pending` covers "uploading" and "checks running". */
export type UploadStatus = 'pending' | 'review' | 'approved' | 'rejected' | 'failed';

/** §2 detected payload family; null until the checks have run. */
export type UploadKind = 'spatial' | 'tabular' | 'indicator_values';

/** §2 detected file format; null until the checks have run. */
export type UploadFormat = 'geotiff' | 'shapefile' | 'gpkg' | 'geojson' | 'csv' | 'xlsx';

/** §2 one automatic check. `blocking` decides whether a failure stops approval. */
export interface UploadCheck {
  name: string;
  passed: boolean;
  blocking: boolean;
  detail?: string | null;
}

/**
 * §2 `checks_meta` — whatever the validator could read out of the file.
 *
 * Every key is optional by contract: a CSV has `columns`/`rows`, a raster has
 * `crs`/`extent`/`bands`, and an upload whose checks have not run yet has none of them.
 * The wizard therefore prints only the keys it finds rather than a table of blanks.
 */
export interface UploadChecksMeta {
  crs?: string;
  extent?: [number, number, number, number];
  bands?: number;
  dtype?: string;
  nodata?: number | null;
  stats?: Record<string, number>;
  geometry_type?: string;
  feature_count?: number;
  columns?: string[];
  /**
   * Tabular: the first 100 rows themselves (§2), **not** a count — `row_count` is the count.
   *
   * `validate_tabular.py` pads or trims every row to the header before storing it, so a row
   * here is positional and as long as `columns`, and the two can be read together by index.
   */
  rows?: UploadPreviewRow[] | IndicatorSubmissionRow[];
  row_count?: number;
  /**
   * `rows` was trimmed to `MAX_META_ROWS` — the counts above still cover every row
   * (`apps.indicators.submissions`).
   */
  rows_truncated?: boolean;
  /**
   * A raster's pixel size as a person reads it, and the same number in metres.
   *
   * Always a ground distance, never the transform's own units: a geographic raster's
   * degrees are converted at its centre latitude before they are printed, so the string is
   * `250 m`, `1.2 km`, or — where the two pixel edges differ by more than 5% — both of
   * them, `100 m × 400 m`. `resolution_m` is then the coarser axis, because that is what
   * such a raster can actually resolve. The wizard's Resolution field is prefilled from
   * the printable one, which is why the validator prints it rather than leaving the form
   * to format a pair of floats.
   */
  resolution?: string;
  resolution_m?: number;
  preview_tiles?: string;
  /** A new version of a vector or a table: its columns against the current version's. */
  schema?: UploadSchemaDiff;
  /* --- kind `indicator_values` (§D3 manual submissions) --------------------------- */
  /** The indicator the submitted CSV is about, echoed from the upload's metadata. */
  indicator_id?: string;
  period?: number;
  level?: SubmissionLevel;
  country?: string | null;
  /** What approving the file would do to the table on file, counted row by row. */
  diff?: IndicatorSubmissionDiff;
}

/**
 * One row of an `indicator_values` submission's `checks_meta.rows`.
 *
 * Keyed by name rather than positional — unlike `UploadPreviewRow`, which is a tabular
 * upload's raw cells — because the shape here is the validator's own reading of the file
 * (`unit_id`, `value`, `note`) placed beside what is already on file (`current`,
 * `previous`), and a reviewer reads those five as a sentence, not as a header row.
 */
export interface IndicatorSubmissionRow {
  unit_id: string;
  name: string;
  value: number | null;
  note: string;
  current: number | null;
  previous: number | null;
}

/** `checks_meta.diff` — every row of the submission, sorted into four buckets. */
export interface IndicatorSubmissionDiff {
  added: number;
  changed: number;
  unchanged: number;
  blank: number;
}

/** `checks_meta.schema` (client, 2026-09-19) — what `schema_changed` found. Renames are guesses. */
export interface UploadSchemaDiff {
  changed: boolean;
  kept: string[];
  added: string[];
  removed: string[];
  renamed: Array<{ from: string; to: string }>;
  previous: string[];
  /** Removed columns a layer's cartography names — the check is blocking when non-empty. */
  dependents?: string[];
}

/**
 * One row of the §2 tabular preview: the cells in `columns` order.
 *
 * Positional rather than keyed by column name, because `columns` is what the validator read
 * out of the header and duplicate or blank headers are a *check failure* there, not a reason
 * for the preview to lose cells to a colliding key.
 */
export type UploadPreviewRow = unknown[];

/** §5 the metadata the wizard collects; free-form on the wire, typed here for the form. */
export interface UploadMetadata {
  title?: string;
  domain?: string;
  /** The server's own name for the source organisation (`uploads.METADATA_FIELDS`). */
  source_org?: string;
  licence?: string;
  cadence?: string;
  abstract?: string;
  lineage?: string;
  contact?: string;
  /** The provenance four (client, 2026-09-22) — `uploads.METADATA_FIELDS` knows them all. */
  source_detail?: string;
  capture_method?: string;
  accuracy?: string;
  resolution?: string;
  classification?: string;
  website_visible?: boolean;
  /** A raster whose pixel values are classes — the server's `categorical`. */
  categorical?: boolean;
  /** Who is submitting (client, 2026-09-18) — required on create, editable here. */
  submitter_name?: string;
  submitter_email?: string;
  /** The dataset this upload adds a version to; written to `Upload.dataset`. */
  dataset_id?: string | null;
  [key: string]: unknown;
}

/**
 * The plan's `UploadRow` — the one shape every uploads endpoint answers with.
 *
 * `parts` is the number of parts the server has **accepted**, which is exactly where a
 * resumed upload starts: part `n` is the `n`-th `part_size` slice of the file, zero-based,
 * so `parts` doubles as "the next part to send" (see `lib/upload.ts`).
 */
export interface UploadRow {
  id: number;
  filename: string;
  size_bytes: number;
  bytes_received: number;
  parts: number;
  part_size: number;
  kind: UploadKind | null;
  format: UploadFormat | null;
  status: UploadStatus;
  metadata: UploadMetadata;
  checks: UploadCheck[];
  checks_meta: UploadChecksMeta;
  dataset_id: string | null;
  /**
   * The person who submitted this upload (client, 2026-09-18).
   *
   * Optional on this side rather than required: the row is also what a pre-round-2 session
   * reads back as, and a queue that threw on an older row would hide the whole queue for
   * one missing name.
   */
  submitter_name?: string | null;
  submitter_email?: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  decided_at: string | null;
  review_comment: string | null;
  /** Present on a create response; the client refuses a bigger file before sending a byte. */
  max_bytes?: number;
}

export interface UploadsResponse {
  count: number;
  results: UploadRow[];
}

/**
 * What `POST /uploads` actually answers: `{id, part_size, parts, max_bytes, status}`.
 *
 * A *subset* of `UploadRow`, and typed as one rather than as the whole row, because the
 * session does not exist yet in the sense the row describes — there is no filename on disk,
 * no checks and no metadata until parts arrive. Everything the transfer needs is here.
 */
export interface UploadSession extends Partial<UploadRow> {
  id: number;
  part_size: number;
  parts: number;
  status: UploadStatus;
  /** The server's cap, echoed on create; absent on a row read back later. */
  max_bytes?: number;
}

/** §1 `POST /uploads` — opens a session and answers the `part_size` the client must use. */
export const createUpload = (body: {
  filename: string;
  size_bytes: number;
  kind_hint?: string | null;
  dataset_id?: string | null;
  /** Required by the server (client, 2026-09-18): the session records who is submitting. */
  submitter_name?: string | null;
  submitter_email?: string | null;
}) =>
  sendJson<UploadSession>('/uploads', {
    filename: body.filename,
    size_bytes: body.size_bytes,
    ...(body.kind_hint ? { kind_hint: body.kind_hint } : {}),
    ...(body.dataset_id ? { dataset_id: body.dataset_id } : {}),
    ...(body.submitter_name ? { submitter_name: body.submitter_name } : {}),
    ...(body.submitter_email ? { submitter_email: body.submitter_email } : {}),
  }).then((row) => row as UploadSession);

/**
 * §1 `PUT /uploads/{id}/parts/{n}` — **not** `sendJson`: the body is the raw slice.
 *
 * A part is bytes, not JSON, so it goes out as `application/octet-stream` with the part's
 * own sha-256 in `X-Part-Sha256` (the server verifies before it appends). Everything else
 * — the CSRF header, the error envelope, the request id — matches `sendJson`, because the
 * failure of a part has to be as traceable as the failure of any other call.
 */
export async function putPart(
  id: number,
  n: number,
  body: Uint8Array | Blob,
  sha256: string,
  init?: { signal?: AbortSignal },
): Promise<{ parts: number; bytes: number }> {
  const token = await ensureCsrf();
  const res = await fetch(`${API_BASE}/uploads/${id}/parts/${n}`, {
    method: 'PUT',
    credentials: 'same-origin',
    signal: init?.signal,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/octet-stream',
      'X-Part-Sha256': sha256,
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
    body: body as BodyInit,
  });
  const requestId = res.headers.get('X-Request-Id');
  if (!res.ok) {
    let envelope: ApiErrorBody = {};
    try {
      envelope = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error page — fall through to the status text */
    }
    throw new ApiError(
      res.status,
      envelope.error?.code ?? `http_${res.status}`,
      envelope.error?.message ?? res.statusText ?? 'Request failed',
      envelope.error?.details ?? null,
      requestId,
    );
  }
  return (await res.json()) as { parts: number; bytes: number };
}

/** §1 `POST /uploads/{id}/finalize` — verifies the whole-file sha and queues the checks. */
export const finalizeUpload = (id: number, sha256: string) =>
  sendJson<UploadRow>(`/uploads/${id}/finalize`, { sha256 }).then((row) => row as UploadRow);

/** §5 `POST /uploads/{id}/metadata` — the wizard's form, saved on every step change. */
export const saveUploadMetadata = (id: number, metadata: UploadMetadata) =>
  sendJson<UploadRow>(`/uploads/${id}/metadata`, metadata).then((row) => row as UploadRow);

export type UploadKey = readonly ['upload', number];
export type UploadsKey = readonly ['uploads', string | null];

/**
 * §1 `GET /uploads/{id}` by id — what `lib/upload.ts` calls to find where a resume starts.
 *
 * Split out of the `QueryFunction` below because `resumeUpload` is not a react-query
 * caller: it has an id and wants a row, and faking a query context to get one would be a
 * lie about who is asking.
 */
export const fetchUploadById = (id: number) =>
  getJson<UploadRow>(`/uploads/${id}`, { credentials: 'same-origin' });

/** §1 `GET /uploads/{id}` as a query function — the wizard polls it while the checks run. */
export const fetchUpload: QueryFunction<UploadRow, UploadKey> = ({ queryKey: [, id] }) =>
  fetchUploadById(id);

/** §1 `GET /uploads?status=` — the review queue (Task 6). */
export const fetchUploads: QueryFunction<UploadsResponse, UploadsKey> = ({
  queryKey: [, status],
}) => getJson<UploadsResponse>(`/uploads${qs({ status })}`, { credentials: 'same-origin' });

/**
 * §2 `GET /uploads/{id}/preview.geojson` — the vector preview (≤ 2 000 simplified features).
 *
 * Exposed as a URL as well as a query function because the review modal's mini-map takes the
 * parsed collection (`L.geoJSON`) while a "download what you are looking at" link would want
 * the address; both must name the same endpoint, and one of them being a hand-built string
 * somewhere in `features/manager` is how they come to disagree.
 */
export const uploadPreviewGeojsonUrl = (id: number): string =>
  `${API_BASE}/uploads/${id}/preview.geojson`;

export type UploadPreviewKey = readonly ['uploadPreview', number];

/** §2 the same endpoint as a query function — what `manager/Preview.tsx` draws. */
export const fetchUploadPreview: QueryFunction<FeatureCollection, UploadPreviewKey> = ({
  queryKey: [, id],
}) =>
  getJson<FeatureCollection>(`/uploads/${id}/preview.geojson`, { credentials: 'same-origin' });

/** §3 the two decisions a reviewer can take. */
export type UploadDecisionAction = 'approve' | 'return';

/**
 * §3 `POST /uploads/{id}/decision` — `approve` publishes, `return` sends it back.
 *
 * Answers the updated `UploadRow` (carrying `dataset_id` on an approve). The comment is
 * optional on the wire and mandatory in the UI for a `return`, which is a review rule rather
 * than a transport one: the submitter has to be told what to fix.
 */
export const decideUpload = (
  id: number,
  body: { action: UploadDecisionAction; comment?: string },
) => sendJson<UploadRow>(`/uploads/${id}/decision`, body).then((row) => row as UploadRow);

/** §1 `DELETE /uploads/{id}` — 204, refused once the upload is approved. */
export const deleteUpload = (id: number) =>
  sendJson<null>(`/uploads/${id}`, undefined, 'DELETE').then(() => undefined);

export const queryKeys = {
  me: () => ['me'] as const,
  /**
   * The three **catalogue** keys carry the language: their bodies are translated
   * server-side (indicator names, layer names, domain labels), so the English and the
   * French answer are two different payloads and both are worth keeping. The default
   * argument means no call site has to pass it, and it comes from the same
   * `currentLanguage()` the fetcher uses — key and URL cannot drift apart.
   *
   * `units`, `sites` and `values` are **not** keyed by language and must not be: their
   * bodies are geometry and numbers, identical in both languages, and a language key there
   * would buy a second 2 MB copy of every admin level for a payload that did not change.
   * (`unit.level_label` is the one label the API still answers in English — Part 3 covers
   * the catalogue, not the hierarchy — see docs/NOTES.md.)
   */
  config: (lang: Language = currentLanguage()): ConfigKey => ['config', lang],
  /**
   * §F4. Language-keyed for the same reason `config` is: the donut's slice labels are
   * translated server-side, so the two languages are two payloads. `activityPoints` below
   * is **not**, because its body is coordinates and counts — identical in both.
   */
  overview: (country: Country, lang: Language = currentLanguage()): OverviewKey =>
    ['overview', country, lang],
  ticker: (lang: Language = currentLanguage()): TickerKey => ['ticker', lang],
  // `by` is in the key because the two groupings are two payloads over the same scope: the
  // unit answer is 65 communes, the watershed answer is 11 basins, and serving one for the
  // other would shade the splash from geometry it does not have.
  activityPoints: (country: Country, by: ActivityGrouping = 'unit') =>
    ['activityPoints', country, by] as const,
  /**
   * S2's pathway chart. **Language-keyed** — the pathway names are translated server-side —
   * and keyed by nothing else, which is the point: the chart recalculates for the map extent
   * and the selection out of this one cached payload, so panning never invalidates it.
   */
  pathways: (country: Country, lang: Language = currentLanguage()): PathwaysKey =>
    ['pathways', country, lang],
  /**
   * The explorer's **All** theme — `GET /activities/by-country`.
   *
   * Keyed by the **grouping** (one per §F5.8 boundary row, since the hover card moved onto
   * the boundaries), by the language (translated category labels) and by the three explorer
   * filters the client asked the counts to follow: the country selector, the selected unit
   * and the year. `unit` is `null` for "no unit selected", which is a *different* answer
   * from any unit's, not a default one — so it is in the key rather than folded away.
   */
  activitiesByUnit: (
    group: ActivityGroup,
    country: Country,
    unit: string | null,
    year: number | null,
    lang: Language = currentLanguage(),
  ): ActivitiesByUnitKey => ['activitiesByUnit', group, country, unit, year, lang],
  /**
   * S2's target bars. Not language-keyed: the body is numbers and unit ids, and the one
   * piece of prose in it (`source`) is a citation of an IFDC document, not a translated
   * label — the same string in both languages.
   */
  targets: (
    ind: string,
    level: ReportingLevel,
    year: number,
    country: Country,
  ): TargetsKey => ['targets', ind, level, year, country],
  // `simplify` is part of the key, not just the request: two rows can ask for the same
  // type/level/country at different generalisations, and without it the second one would
  // be served the first one's cached geometry.
  //
  // `parent` (§F5.8) is in the key for the same reason and a sharper one: the scoped and
  // unscoped answers for `(admin, 2, BFA)` are *different sets of features*, and serving one
  // for the other would paint the whole country under a filter that says one region. It is
  // the last element and defaults to `null`, so every unscoped key is what it always was
  // plus a trailing `null` — one shape, and no call site outside this object to keep in step.
  units: (
    type: UnitType,
    level: number | null,
    country: Country,
    simplify: Simplify = 'z7',
    parent: string | null = null,
  ) => ['units', type, level, country, simplify, parent] as const,
  /**
   * §F5.8 cascade dropdowns — names only, so `simplify` has no meaning here.
   *
   * `operational` is part of the key (R6): the filtered and unfiltered answers are two
   * different lists for the same `type/level/country/parent`, and caching them as one would
   * serve whichever arrived first.
   */
  unitOptions: (
    type: UnitType,
    level: number | null,
    country: Country,
    parent: string | null = null,
    operational = true,
  ) => ['unitOptions', type, level, country, parent, operational] as const,
  sites: (country: Country) => ['sites', country] as const,
  search: (q: string, country: Country, parent: string | null = null) =>
    ['search', q, country, parent] as const,
  indicators: (lang: Language = currentLanguage()): IndicatorsKey => ['indicators', lang],
  /**
   * §D3 `GET /indicators/{id}/current` — the manual values panel's prefill.
   *
   * All four selects are in the key: a period, a level or a country change is a different
   * set of units with different numbers in them, and sharing one entry would prefill the
   * grid from the answer to a question nobody asked.
   */
  indicatorCurrent: (
    id: string,
    period: number,
    level: string,
    country: string | null = null,
  ) => ['indicatorCurrent', id, period, level, country] as const,
  /**
   * §D4 `GET /datasets` — the catalogue, whole or narrowed to one `source_org`.
   *
   * `source` is part of the key so a narrowed read never overwrites the whole-catalogue
   * entry, and so re-ticking an organisation is a cache hit. §F8 passes the one ticked
   * source (and `null` for none or several — see `fetchDatasets`); every other reader of
   * the catalogue, `app/RecordHost.tsx` included, passes nothing.
   */
  datasets: (lang: Language = currentLanguage(), source: string | null = null): DatasetsKey =>
    ['datasets', lang, source],
  /** §D4 `GET /datasets/{id}` — the §F8 detail drawer. */
  dataset: (id: string, lang: Language = currentLanguage()): DatasetKey => ['dataset', id, lang],
  /**
   * M6 §4 `GET /datasets?include_hidden=1` — the Data Manager's list.
   *
   * Language-keyed like `datasets`, and deliberately a *different* key: this answer carries
   * unpublished rows, and sharing the catalogue's entry would leak them onto §F8.
   */
  datasetsAll: (lang: Language = currentLanguage()): DatasetsAllKey => ['datasetsAll', lang],
  /** §2 `GET /datasets/{id}/rows` — one page of the record's Data tab. */
  datasetRows: (
    id: string,
    params: DatasetRowsParams,
    lang: Language = currentLanguage(),
  ): DatasetRowsKey => ['datasetRows', id, params, lang],
  /**
   * The Data Manager's editable grid — `GET /datasets/{id}/rows/all`.
   *
   * Language-keyed like the paged read (the column labels are translated server-side) and
   * keyed by nothing else: the read is the whole table, so there is no page or ordering for
   * two entries to differ over.
   */
  datasetRowsAll: (id: string, lang: Language = currentLanguage()): DatasetRowsAllKey =>
    ['datasetRowsAll', id, lang],
  /**
   * §F7 `GET /dashboard` — language-keyed like the other translated catalogue bodies
   * (`kpis[].label`, `insights[].text`, … are server-translated), and by every scope axis
   * the payload varies over so two countries, years or domains never collide.
   */
  dashboard: (
    country: Country,
    year: number,
    domain: string | null,
    /**
     * §F7 scope (client item 7). Last before the language and defaulted, so every existing
     * call site keeps the key it had plus one `null` — and a unit-scoped payload can never
     * be served out of the country-scoped entry, which is a different set of numbers for
     * the same country/year/domain.
     */
    unit: string | null = null,
    /**
     * §3 the survey round. Defaulted like `unit` and in front of the language for the same
     * reason: a caller that does not care keeps the key it had plus one `null`, and two
     * rounds of the survey are two different payloads for one country/year/domain.
     */
    baselinePeriod: number | null = null,
    lang: Language = currentLanguage(),
  ): DashboardKey => ['dashboard', country, year, domain, unit, baselinePeriod, lang],
  // `mode` is part of every values/series key: the annual and the cumulative readings of
  // the same indicator-year are two different answers, and they are both worth caching —
  // a reader ticking the box on and off must not pay for a refetch each way.
  values: (
    ind: string,
    level: ReportingLevel,
    year: number,
    country: Country,
    mode: ValueMode = ANNUAL,
    parent: string | null = null,
  ) => ['values', ind, level, year, country, mode, parent] as const,
  /** §F5.7 spark — the series is year-independent, so the year is *not* in the key. */
  series: (ind: string, unit: string, mode: ValueMode = ANNUAL) =>
    ['series', ind, unit, mode] as const,
  /** §F5.9 dock popover — the one scope-wide number for a year. */
  programmeValue: (ind: string, year: number, country: Country, mode: ValueMode = ANNUAL) =>
    ['programmeValue', ind, year, country, mode] as const,
  /** §F5.9 popover spark. Year-independent like `series`, so a year tick is a cache hit. */
  programmeSeries: (ind: string, country: Country, mode: ValueMode = ANNUAL) =>
    ['programmeSeries', ind, country, mode] as const,
  /** §F5.7 "Other indicators". `ids` is joined so the key stays a flat, stable tuple. */
  bulk: (ids: string[], level: number, year: number, country: Country) =>
    ['bulk', ids.join(','), level, year, country] as const,
  /** §D2 unit detail — name, level label, parents and bbox for one unit. */
  unit: (id: string) => ['unit', id] as const,
  /**
   * Task 6: the four `intelligence/…` reads whose bodies carry translated prose (titles,
   * questions, driver notes, methodology text) — language-keyed for the same reason
   * `indicators`/`layers` are. `intelUnits` (the choropleth's `{unit_id, category, score}`
   * rows) and `intelConfig` (raw config values, edited as JSON in the settings table) are
   * not: nothing in either body is translated prose.
   */
  intelUseCases: (lang: Language = currentLanguage()) => ['intelUseCases', lang] as const,
  intelSummary: (useCase: string, lang: Language = currentLanguage()) =>
    ['intelSummary', useCase, lang] as const,
  intelUnits: (
    useCase: string,
    level: string,
    category: string | null = null,
    country: Country | null = null,
  ) => ['intelUnits', useCase, level, category, country] as const,
  intelUnit: (useCase: string, unitId: string, lang: Language = currentLanguage()) =>
    ['intelUnit', useCase, unitId, lang] as const,
  intelMethodology: (useCase: string, lang: Language = currentLanguage()) =>
    ['intelMethodology', useCase, lang] as const,
  intelConfig: (useCase: string) => ['intelConfig', useCase] as const,
  /** Amendment 1 §1: the Help tab's cards — translated prose, language-keyed like `intelUseCases`. */
  intelHelp: (lang: Language = currentLanguage()) => ['intelHelp', lang] as const,
  /** Task 7: UC2's own reads — language-keyed like `intelUnit`, same translated-prose reason. */
  intelSimilar: (
    useCase: string,
    unitId: string,
    k: number,
    group: string | null,
    lang: Language = currentLanguage(),
  ) => ['intelSimilar', useCase, unitId, k, group, lang] as const,
  intelCompare: (useCase: string, a: string, b: string, lang: Language = currentLanguage()) =>
    ['intelCompare', useCase, a, b, lang] as const,
  /** UC3's own reads — not language-keyed: `insight`/`limitations` carry both `en`/`fr`. */
  intelDiagnosis: (crop: string, year: number, country: Country | null) =>
    ['intelDiagnosis', crop, year, country] as const,
  intelSeries: (unit: string, year: number) => ['intelSeries', unit, year] as const,
  intelUc3Config: () => ['intelUc3Config'] as const,
  /** Amendment 1 §3.3: the stepper's own step list, geography levels and rainfall policy. */
  intelDefinition: (useCase: string) => ['intelDefinition', useCase] as const,
  /**
   * §D5 `GET /layers` — **role-keyed**. The server computes `access` per §I1 and omits
   * NONE rows for public/field/partner (§I3), so two roles get two different bodies from
   * one URL. Without the role in the key the §F3 demo switcher would keep showing the
   * previous role's catalogue out of cache.
   */
  layers: (role: Role, lang: Language = currentLanguage()): LayersKey => ['layers', role, lang],
  /**
   * §1a rule 4 display outline. Not keyed by role or language: it is geometry, identical
   * for every reader, and it changes only when a boundary version is approved.
   */
  programmeExtent: () => ['programmeExtent'] as const,
  /** §D5 `GET /layers/{id}/features?country=` — the country is a request parameter. */
  /**
   * `bbox` is part of the key because it is part of the request (§D5 `?bbox=`). It is
   * serialised rather than kept as an array so two equal boxes are one cache entry —
   * react-query hashes the key structurally, and `VectorLayers.viewportBbox` snaps the
   * numbers so a small pan produces the same string.
   */
  layerFeatures: (id: string, country: Country, bbox: FeatureBbox | null = null) =>
    ['layerFeatures', id, country, bbox ? bbox.join(',') : null] as const,
  /**
   * §D6 `GET /analysis/results/{job_id}.{ext}` — a finished run's artefact (M4).
   *
   * Keyed by the **job**, not by the URL, and by nothing else: a job's artefact is written
   * once and never rewritten, so two readers of the same result share one fetch and a
   * re-registered result layer costs none at all. Not role-keyed either — §I3 decides
   * whether the *download* is permitted, and a refusal is an error rather than a second
   * body worth caching.
   */
  analysisResult: (jobId: string, ext = 'geojson') => ['analysisResult', jobId, ext] as const,
  /**
   * S3's Analytics & Insights panel — `GET /analysis/insights` (`apps/analysis/insights.py`).
   *
   * Every input the route takes is in the key, language included: the class labels, the
   * country names and the layer name are translated server-side, so the English and the
   * French answers are two payloads. Not role-keyed — §I3 refuses a layer a role may not
   * read rather than serving a second, thinner body of it.
   *
   * `polygon` is §F5.10's drawn ring, already serialised, so the key stays a tuple of
   * scalars and a redrawn ring is a different key rather than a stale hit. Where it is set
   * the server ignores `unit`/`country`, and the caller sends them as `null` to match — two
   * keys for one answer would be a cache that lies.
   */
  insights: (
    layer: string,
    unit: string | null,
    country: Country | null,
    polygon: string | null,
    level: ReportingLevel,
    top: number,
    lang: Language = currentLanguage(),
  ) => ['insights', layer, unit, country, polygon, level, top, lang] as const,
  /**
   * M6 §1 `GET /uploads/{id}` — one upload session, keyed by its numeric id and nothing
   * else. Not language-keyed: the body is filenames, byte counts and check names, the same
   * payload in both languages (the wizard translates the check names on this side).
   */
  upload: (id: number): UploadKey => ['upload', id],
  /**
   * M6 §1 `GET /uploads?status=` — the review queue. `status` is in the key because it is
   * in the request: "everything" and "in review" are two different lists, and serving one
   * out of the other's entry would show a reviewer a queue that is not theirs.
   */
  uploads: (status: string | null = null): UploadsKey => ['uploads', status],
  /** M6 §2 `GET /uploads/{id}/preview.geojson` — the vector preview, keyed by upload id. */
  uploadPreview: (id: number): UploadPreviewKey => ['uploadPreview', id],
};

/** §F1: "staleTime 5 min". One client for the whole app, created in App.tsx. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // A 4xx is the caller's fault - never retry it.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });
}
